import { ChangeDetectorRef, ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { UnsavedChangesGuard, UnsavedChangesGuardProvider, CanLeaveResult } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { AuthService } from 'services/auth.service';
import { NutrientDefinitionsOrdered } from '@cf-shared/data-model/display-nutrient';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { FoodDbService, FOOD_DB_READY_TIMEOUT_MS } from 'services/food-db.service';
import { FoodReadFailedError, FoodReadFailurePolicy } from 'services/food-read-error';
import { isDatabaseUnavailable } from 'services/database-unavailable-error';
import { isNetworkError } from 'services/cloud-functions-errors';
import { UserDb } from 'services/user-db.service';
import { ScaledImage } from '@cf-shared/data-model/scaled-image';
import { PublishFoodResults } from '@cf-shared/publish-food-interface';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { FlagContentService } from 'services/flag-content.service';
import { StapleDbService } from 'services/staple-db.service';
import { StaplePictureFiles } from 'services/staple-picture-files.generated';
import { staplePictureFor } from 'services/staple-picture-lookup';
import { VldGroupDirective } from 'directives/vld-group/vld-group';
import { VldGroupState } from 'directives/vld-group/vld-group-interface';
import { DatabaseWrapper, FoodUpdatedEvent } from 'services/database-wrapper';
import { AlertButton, AlertController, AlertOptions, AlertButtonRole } from 'providers/alert-controller';
import { deepCheckValid, deepClone, deepCompareEQ, deepOverwriteCouchSafe, CouchDocDraft } from '@cf-shared/deep-helpers';
import * as crypto from 'crypto';
import { SettingsService } from 'services/settings.service';
import { ImageService } from 'services/image-service';
import { ButtonType, NutritionLabelStyle } from '@cf-shared/enums';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { trySystemShare } from 'services/system-share';
// the nutrition label's own calculations, lifted out of this class so they can be unit
// tested without a browser (df-ea475c9c5d44)
import {
  MacrosDisplayMode,
  NutritionLabelEntry,
  NutritionLabelOrder,
  attachDisplayNutrients,
  buildMacrosPerLabel,
  buildNutritionHeaderDescription,
  buildNutritionLabelOrder,
  buildServingSegmentLabel,
  buildServingSizeTexts,
  computeStandardAmount,
  findServingMeasure,
  formatEnergyDisplayValue,
  formatNutrientDisplayValue,
  reconcileTotalCarbs,
  scaleEditedNutrientsToAmount,
  selectOptionalNutrients,
  storeEditedNutrientsAsPer100g,
  synthesizeCarbTotDisplay,
} from './nutrition-label';
import { scaleNutrientsToPer100g, scaleNutrientsToServing } from 'util/nutrient-scaling';

export enum RecipeTab {
  Overview = 'overview',
  Foods = 'foods',
}

// unified slot for automatic mode - combines isReadOnly with selectedRecipeTab
export type RecipeSlot = 'view-overview' | 'view-foods' | 'edit-overview' | 'edit-foods';

import { NutrientBarStyle } from 'components/nutrient-list/nutrient-list';
import { LoggingService } from 'services/logging.service';
import { FormatNutrients, isReservedMeasureName } from 'util/formatting';
import { TelemetryService } from 'services/telemetry.service';
import { PubSubService } from 'services/pubsub.service';
import { LoadingController } from 'providers/loading-controller';
import { checkNutrientCalories, INFOODS, Food, Nutrients, FoodMeasure, FoodStatus, generateUserFoodId, FoodSourceInfo, foodSourceInfoTable, FoodSource, getFoodSource, isLegacyMealId, isNutrientValueEntered } from '@cf-shared/data-model/food';
import { isIngredientReal, Ingredient, IngredientReal, IngredientType } from '@cf-shared/data-model/ingredient';
import { Recipe, isRecipe, RECIPE_SERVINGS_MEASURE_NAME } from '@cf-shared/data-model/recipe';
import { ToastController } from 'providers/toast-controller';
import { EditIngredientResult, IEditIngredientPage } from 'pages/edit-ingredient/edit-ingredient-interface';
import { buildLogPortionScreenParams } from 'pages/edit-ingredient/log-mode-open';
import { IngredientEditReturnContext } from 'components/ingredient-search/ingredient-add-telemetry';
import { DebugService } from 'services/debug.service';
import { PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { IngredientList } from 'components/ingredient-search/ingredient-list';
import { IngredientListLoadResult } from 'components/ingredient-search/ingredient-list-load';
import { FoodsPageResult } from 'pages/foods/foods-interface';
import {
  AddFoodErrand, ErrandAnswerer, ErrandAnswerShape, answerAddFoodErrandAsync, errandLeaveAnswer,
  finishDayErrandOnLeave, readAddFoodErrand,
} from 'pages/foods/add-food-errand';
import { IBarcodeScannerPage } from 'pages/barcode-scanner-page/barcode-scanner-interface';
import { IHomePage } from 'pages/home/home-interface';
import { IRecipeScannerPage, RecipeScannerResult, pendingRecipeScanImageDataUrl, setPendingRecipeScanImage } from 'pages/recipe-scanner-page/recipe-scanner-interface';

import {
  EditFoodPageParams, FoodDetailsExit, FoodDetailsResult, IEditFoodPage, pendingLabelScanImageDataUrl, setPendingLabelScanImage,
} from './edit-food-interface';
import { canReportFood } from 'util/can-report-food';
import { BarcodePairing, BarcodeQuestion, decideBarcodePairing } from './barcode-pairing';
import { getFoodSourceDisplay, getFoodSourceShortName } from './food-source-display';
import { buildStapleSourceRows } from './staple-source-rows';
import { resolveRecipeMeasures, stripRecipeServingsMeasure } from './recipe-measures';
import { pagesToUrl } from 'utils/url-parser';
import { ToastType } from 'components/toast/toast-host';
import { describeError } from '@cf-shared/error-describe';
import { isTransientFetchError } from 'services/error-patterns';
import { isTransportFailureAfterRetries } from 'services/cloud-functions-retry';
// telling a failed unpublish that matters apart from the ordinary one, lifted out of this
// class so it can be unit tested without a browser (df-274672bb1f5d)
import { UnpublishOutcome, describeUnpublishOutcome } from './unpublish-outcome';
import { RecipeShareRole, recipeShareRole } from './recipe-share-role';
import { adoptSharedRecipeCopyAsync, localBlockForForkedCopy } from './forked-food-local';
import { FoodUnpublishOutboxService } from 'services/food-unpublish-outbox.service';
import { resolveEditFoodTarget, EditFoodTarget } from './edit-food-nav-params';
import { saveThroughDraftAsync } from './food-draft-save';
import { mayLeaveWithoutAsking } from './leave-decision';

@Component({
  selector: 'page-edit-food',
  templateUrl: 'edit-food.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
// the result is the bare food id, or a food list result when the caller's errand asks for one (#5377)
export class EditFoodPage extends CsPage<EditFoodPageParams, FoodsPageResult | string> {
  protected LL = LL;
  @ViewChild(VldGroupDirective, { static: true }) vldGroup: VldGroupDirective;

  @ViewChild('ingredientsCard') ingredientsCard: IngredientList;

  /**
   * How many grams the nutrient inputs stand for, and what the save divides by to store them
   * per 100 g. Worked out on every read rather than kept in a field: it must follow the
   * serving size the moment the user changes it, as the hint above the inputs does (#6024).
   * A stored copy was refreshed only when the page opened, so a new food kept 100. Refreshing
   * it from the Serving Sizes card's change event does not work either: that card reports a
   * change BEFORE the typed value is written into the measure, so it lags one keystroke.
   */
  protected get STD_AMOUNT(): number {
    return computeStandardAmount(this.food?.measures, this.macrosDisplayMode);
  }
  protected STD_UNIT: string = 'g';

  // these are used by vld-* components
  protected progressStep = 0;
  protected totalSteps: number;
  checkValues: boolean;

  // Food
  protected food: Food | Recipe;
  // True once ionViewCanEnter has loaded the food this page was opened for. Needed because
  // `this.food` is never empty — the constructor seeds it with a blank placeholder — so it
  // cannot answer "has the real food arrived yet?". See ionViewCanEnter (#4681).
  private loadedFoodFromId = false;
  protected unmodifiedFood: Food | Recipe;
  // Set only while the form is editing a food that came from the database, and
  // cleared the moment the form moves to a food that did not. save() and
  // deleteFoodAsync() both read it to reach the shared document, so a holder left
  // pointing at the wrong food is #5086. Swap `this.food` only through
  // setEditedFood() or beginEditingDraft(), which keep the two in step.
  private foodDraft: CouchDocDraft<Food | Recipe> | undefined;
  private unsavedChangesGuard: UnsavedChangesGuard<FoodsPageResult | string>;
  /**
   * What the form held once the page had finished setting itself up.
   *
   * Deliberately NOT `unmodifiedFood`, which is snapshotted earlier, before the
   * page fills in the empty `image`, `nutrients`, `measures` and
   * `totalWeight_g` a food document may arrive without. Comparing against that
   * earlier copy reports a difference on a food nobody has touched, because a
   * missing value and a zero are not the same thing to `deepCompareEQ` — which
   * would raise the leave dialog on every exit.
   */
  private foodAsLoaded: Food | Recipe;
  /**
   * The id of this food once it has actually been written to the database, and undefined
   * until then (#4968). See `getResult` on the unsaved-changes guard for why the distinction
   * matters: an unsaved food already has an id, so the id alone cannot say whether it exists.
   */
  private savedFoodId: string | undefined;
  /**
   * Set once this page has deleted the food it was editing, and never cleared (df-32c03324a817).
   *
   * `deleteFoodAsync` only closes the page when the page is still the one on screen — the user
   * can walk on to another screen while the unpublish is still retrying, which takes about 30
   * seconds normally and up to ~8.5 minutes when the network hangs. This page is then left sitting
   * underneath whatever they moved to, still showing a food that no longer exists, and Back brings
   * them straight back to it. This flag is what lets the page notice that and close itself, and
   * what stops any of this page's writers from putting the deleted food back into the database in
   * the meantime — see `mustNotWriteDeletedFood`, which every one of them asks first.
   */
  private foodWasDeleted = false;
  /**
   * Set once something has begun closing this editor on behalf of that delete.
   *
   * Two places want to close it and either can run first, so without this latch both can run and
   * two pops fire for one page — the second taking away the screen underneath, which is the very
   * thing `deleteFoodAsync`'s `viewDestroyed` guard exists to prevent. `viewDestroyed` cannot
   * stand in for it: `_navPrepareEnter` clears that flag EARLY in the back transition while
   * `ionViewDidEnter` runs only after the transition has finished, so there is a window in which
   * the page has been re-entered, `viewDestroyed` is already false, and the self-close has not
   * run yet. Whichever of the two gets here first sets this and closes; the other stands down.
   */
  private editorCloseStarted = false;

  // protected (not private) so the create-mode ⋯ menu's template guard
  // `@if (mode === 'createRecipe')` compiles under strict template checking, matching isReadOnly
  protected mode: string;

  /** set when the staple picture could not be loaded, so the page falls back to today's look */
  private staplePictureFailed = false;

  // Nutrients
  protected nutrients: Nutrients;
  protected displayNutrientsTotal: UiDisplayNutrient[];
  protected displayNutrientsPer100g: UiDisplayNutrient[];
  protected displayNutrientsPerServing: UiDisplayNutrient[];

  protected isReadOnly: boolean;

  public nutritionLabelOrderMain: NutritionLabelOrder = { style: NutritionLabelStyle.US, entries: [] };

  // this is used to untangle the US label conundrum where fiber is included in carbs
  private totalCarbsOrg_g: number = undefined;

  foodSource: FoodSourceInfo = foodSourceInfoTable[FoodSource.Unknown]
  // the source badge shows the two app-owned names ("Recipe", "Community")
  // through the translation catalog; the acronym sources stay as published
  foodSourceShortName: TranslatedString = getFoodSourceShortName(FoodSource.Unknown)

  ButtonType = ButtonType;
  NutritionLabelStyle = NutritionLabelStyle;
  // the recipe Overview tab's compact macros card names one of these in its markup
  NutrientBarStyle = NutrientBarStyle;

  // label scan warnings (set when food was created from label scan)
  labelScanConfidence: number = null;
  labelScanNutrientConfidences: { [key: string]: number } = null;
  labelScanJsonRepaired: boolean = false;
  commentsEditable: boolean = false;

  /**
   * What was decided about this food's barcode, while a question about it is still owed to the
   * user (#4968).
   *
   * Decided during page setup but asked afterwards, in `ionViewDidEnter`, so an alert never
   * sits in front of a page that has not drawn yet. Cleared as it is asked, so coming back to
   * this page — from the barcode scanner, from a sub-page — does not ask again.
   *
   * The whole decision is kept, not just the question, because the alert's buttons offer the
   * two candidates and must offer the CLEANED ones the decision was made from.
   */
  private pendingBarcodePairing: BarcodePairing | undefined;

  // View mode state
  macrosDisplayMode: MacrosDisplayMode = 'serving';
  showAdditionalNutrients = false;

  // Import/sharing detection
  protected isImportableRecipe = false;
  protected isImportedRecipe = false;
  protected hasOriginalUpdate = false;
  protected originalDeleted = false;

  // Recipe tab state
  hasClientSmallScreen = hasClientSmallScreen;
  RecipeTab = RecipeTab;
  private _selectedRecipeTab: RecipeTab = RecipeTab.Overview;
  get selectedRecipeTab(): RecipeTab {
    return this._selectedRecipeTab;
  }
  set selectedRecipeTab(tab: RecipeTab) {
    this._selectedRecipeTab = tab;
    // The ingredients tab holds an absolutely-positioned card list (cs-card-list). While its
    // tab is hidden the list has zero height, so any card positions computed then are wrong —
    // e.g. after a recipe scan adds ingredients while the Overview tab is showing. Recompute
    // the layout once the tab becomes visible so the cards land in place without the user
    // having to scroll to nudge them.
    if (tab === RecipeTab.Foods) {
      this.safeTimeout(() => this.ingredientsCard?.updateLayout(), 0);
    }
  }
  animateTabContent = false;

  // derived: unified slot for automatic mode
  get activeRecipeSlot(): RecipeSlot {
    if (this.isReadOnly) {
      return this.selectedRecipeTab === RecipeTab.Overview ? 'view-overview' : 'view-foods';
    }
    return this.selectedRecipeTab === RecipeTab.Overview ? 'edit-overview' : 'edit-foods';
  }

  constructor(
    public navCtrl: NavController,
    public foodService: FoodDbService,
    private auth: AuthService,
    private cloudFunctionsService: CloudFunctionsService,
    public userDb: UserDb,
    private pubSub: PubSubService,
    private cd: ChangeDetectorRef,
    private alertCtrl: AlertController,
    public logging: LoggingService,
    protected settings: SettingsService,
    private loadingCtrl: LoadingController,
    private telemetry: TelemetryService,
    private toastController: ToastController,
    private debug: DebugService,
    
    private popupMenu: PopupMenuProvider,
    private imageService: ImageService,
    private flagContentService: FlagContentService,
    private unsavedChangesGuardProvider: UnsavedChangesGuardProvider,
    private foodUnpublishOutbox: FoodUnpublishOutboxService,
    private stapleDb: StapleDbService,
  ) {
    super();
    // Initialize with a temporary Recipe that has all required properties.
    // assigned directly: no draft holder can exist yet, so there is nothing to keep in step
    this.food = {
      _id: '',
      name: '',
      ingredients: [],
      instructions: '',
      nutrients: {},
      measures: [],
      status: FoodStatus.Private
    } as Recipe;
  }

  hasInstructions() {
    return isRecipe(this.food) && this.food.instructions && this.food.instructions.length > 0;
  }

  getTitle() {
    if (this.food.name) {
      return this.food.name;
    }
    if (this.isRecipe()) {
      return LL.editFood.titles.newRecipe();
    }
    return LL.editFood.titles.newFood();
  }

  isRecipe() {
    if (this.food && this.food._id) {
      return isRecipe(this.food);
    }
    return false;
  }

  /** Helper getter for template - returns food as Recipe when it is a Recipe */
  protected get recipe(): Recipe | undefined {
    return isRecipe(this.food) ? this.food : undefined;
  }

  servingSizeChanged(servingSize: number) {
    if (this.isReadOnly) {
      scaleEditedNutrientsToAmount(this.nutrients, this.food.nutrients, servingSize);
      // the dropped CarbTot is recalculated from the converted CarbNet + Fiber, so forget the
      // remembered original total too
      this.totalCarbsOrg_g = undefined;
    }
    this.cd.markForCheck();
  }

  setNutritionLabelOrder(resetDisplayMode = true) {
    // if resetDisplayMode, set macrosDisplayMode to default for current label style
    // (called from template when user changes label style)
    if (resetDisplayMode) {
      this.macrosDisplayMode = this.settings.nutritionLabelStyle === NutritionLabelStyle.US ? 'serving' : '100g';
    }

    const order = buildNutritionLabelOrder(this.settings.nutritionLabelStyle);

    // the form's nutrient inputs are expressed in STD_AMOUNT grams, which follows the
    // per-serving/per-100g toggle rather than the label style
    this.servingSizeChanged(this.STD_AMOUNT);

    this.nutritionLabelOrderMain = attachDisplayNutrients(order, this.getDisplayedNutrients());

    this.updateTotalCarbs();
    this.cd.markForCheck();
  }

  // this handles the conundrum that US food labels show total carbs (including fiber), instead of true net carbs
  updateTotalCarbs() {
    const reconciled = reconcileTotalCarbs(this.nutrients, this.totalCarbsOrg_g);
    // assign onto the existing nutrients object rather than replacing it -- the template
    // binds two-way into it
    if (reconciled.carbNet != undefined) {
      this.nutrients[INFOODS.CarbNet] = reconciled.carbNet;
    }
    if (reconciled.carbTot != undefined) {
      this.nutrients[INFOODS.CarbTot] = reconciled.carbTot;
    }
    this.totalCarbsOrg_g = reconciled.totalCarbsOrg_g;
  }

  async ionViewDidEnter() {
    // the user has come back to a page whose food this page already deleted, which happens when
    // they walked away while the delete's unpublish was still retrying (df-32c03324a817). There
    // is nothing here to show and nothing to save, so close the page the same way a page that
    // could not load its food closes.
    //
    // This hook and not ionViewWillEnter: cs-nav awaits ionViewWillEnter as part of the page
    // transition itself and only calls ionViewDidEnter once that transition has finished. It is
    // still inside the popstate handler that brought us back, though, which is why the closer
    // has to check for that -- see closeDeletedFoodEditorAsync.
    if (this.foodWasDeleted) {
      await this.closeDeletedFoodEditorAsync();
      return;
    }
    await this.askPendingBarcodeQuestionAsync();
  }

  /**
   * Settle the barcode of a food created from a label scan, by asking the user (#4968).
   *
   * There are two things worth asking about, and `decideBarcodePairing` has already worked out
   * which — if either — applies:
   *
   * - the user scanned a barcode, it found nothing, and the label they then photographed shows
   *   no barcode of its own. Nothing corroborates the pairing, so confirm the label really is
   *   the product they scanned.
   * - both produced a barcode and the two differ. Only the user can say which is right.
   *
   * In both cases the food is ALREADY carrying the barcode the user scanned, because that is
   * the usual answer. So dismissing the alert without choosing keeps it, and only an explicit
   * "no" or an explicit pick of the other one changes anything.
   */
  private async askPendingBarcodeQuestionAsync() {
    const pairing = this.pendingBarcodePairing;
    // asked at most once, whatever brings this page back into view
    this.pendingBarcodePairing = undefined;
    if (!pairing) return;

    // Both candidates come from the decision, never from the navigation parameters, so the
    // barcode a button stores is the same cleaned value the decision was made from.
    const { fromLabel, fromScan } = pairing;

    // a switch rather than a chain of ifs, so a question added to the enum later is caught
    // here instead of quietly falling into the mismatch alert. The `default` below is what
    // does the catching: the repo's `switch-exhaustiveness-check` lint rule is turned off, so
    // without it a new value would simply do nothing.
    switch (pairing.question) {
      case BarcodeQuestion.None:
        return;

      case BarcodeQuestion.ConfirmScanned: {
        const isTheSameProduct = await this.alertCtrl.showAlertAsync<boolean>({
          title: LL.editFood.alerts.confirmScannedBarcode(),
          message: LL.editFood.alerts.confirmScannedBarcodeMessage({ barcode: fromScan }),
          buttons: [
            { text: LL.common.buttons.yes(), role: AlertButtonRole.Primary, result: true },
            { text: LL.common.buttons.no(), role: AlertButtonRole.Cancel, result: false },
          ],
          // dismissing without answering keeps the barcode, which is what the food already has
          implicitCancelResult: true,
        });
        if (!isTheSameProduct) {
          this.setFoodBarcode(undefined);
        }
        return;
      }

      case BarcodeQuestion.ChooseBetween: {
        const chosen = await this.alertCtrl.showAlertAsync<string>({
          title: LL.editFood.alerts.barcodeMismatch(),
          message: LL.editFood.alerts.barcodeMismatchMessage({ scanned: fromScan, onLabel: fromLabel }),
          buttons: [
            { text: LL.editFood.buttons.useScannedBarcode(), role: AlertButtonRole.Primary, result: fromScan },
            { text: LL.editFood.buttons.useBarcodeOnLabel(), role: AlertButtonRole.Cancel, result: fromLabel },
          ],
          implicitCancelResult: fromScan,
        });
        this.setFoodBarcode(chosen);
        return;
      }

      default: {
        // assigning to `never` is what makes this a COMPILE error the day a question is added
        // to the enum without a case here
        const unhandled: never = pairing.question;
        this.logging.trackSilentProblem('UnhandledBarcodeQuestion', { question: String(unhandled) });
        return;
      }
    }
  }

  /** Put one barcode on the food being created, or none at all. */
  private setFoodBarcode(barcode: string | undefined) {
    if (this.viewDestroyed || !this.food) return;
    this.food.EANs = barcode ? [barcode] : [];
    this.cd.markForCheck();
  }

  /**
   * Load the food this page was opened for, and refuse the page if it cannot be shown.
   *
   * This has to happen HERE rather than in ngOnInit. `failPageGuardAsync` works by throwing,
   * and only a throw from this hook actually cancels the navigation — cs-nav runs it before the
   * page is put on screen. ngOnInit runs after the page is committed, where Angular ignores the
   * rejected promise: the page then stayed on screen showing an empty "New food" form, which is
   * exactly what the customer in #4681 was left looking at. Every other page that uses this
   * guard already loads its record here; edit-food was the only one that did not.
   */
  async ionViewCanEnter() {
    // This hook runs again on every return from a sub-page. By then the food is loaded and the
    // user may have unsaved edits in the draft, so re-loading it would throw their work away.
    //
    // The test is this explicit flag and NOT `if (this.food)`, which looks equivalent and is
    // not: the constructor fills `this.food` with a blank placeholder recipe so the template
    // has something to bind to before anything is loaded. That placeholder is why a food that
    // failed to load rendered as an empty "New food" form rather than a blank screen — and a
    // guard written against it is true on the very first entry, which would silently skip the
    // load this hook exists to do.
    if (this.loadedFoodFromId) {
      return;
    }

    let target: EditFoodTarget;
    try {
      target = resolveEditFoodTarget(this.navParams.data);
    } catch (err) {
      // the id looked like the packed create-a-food form but would not parse — a corrupt link
      return this.auth.failPageGuardAsync(`Could not read the food to open: ${describeError(err)}`);
    }

    // no id: the page is creating a new food or recipe, which ngOnInit sets up
    if (!target.id) {
      return;
    }

    // Opening the app straight from a shared food link runs this page during boot, before the
    // shared food databases have started — so wait for them rather than reporting a food that
    // exists as missing. This is what makes a shared recipe link actually open; without it
    // every such link failed, which is what the customer in #4681 ran into.
    if (!await this.foodService.awaitReadyWithTimeoutAsync(FOOD_DB_READY_TIMEOUT_MS)) {
      return this.auth.failPageGuardAsync(
        `Food database was not ready within ${FOOD_DB_READY_TIMEOUT_MS} ms while opening '${target.id}'`);
    }

    let food: Food;
    try {
      // Throw so a failed READ arrives as an error rather than as null. Without it
      // "this food does not exist" and "we could not read it" are the same answer, and neither
      // we nor the user can tell them apart afterwards — which is a large part of why #4681
      // left no usable trace of what had gone wrong.
      food = await this.foodService.getFoodByIdAsync(target.id, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.Throw });
    } catch (err) {
      return this.auth.failPageGuardAsync(`Could not read food '${target.id}': ${describeError(err)}`);
    }
    if (food == null) {
      return this.auth.failPageGuardAsync(`Unknown food id: '${target.id}'`);
    }
    this.setEditedFood(food);
    this.loadedFoodFromId = true;
  }

  async ngOnInit() {
    // OnPush change detection handles this automatically

    const { id, ean, name } = resolveEditFoodTarget(this.navParams.data);

    const mode = this.navParams.data.mode || '';
    this.isReadOnly = true;

    if (id) {
      // ionViewCanEnter above has already loaded this food, and refuses the page when it
      // cannot — so reaching here without it having done so means the page was shown without
      // that hook running. Report it rather than carrying on with the constructor's blank
      // placeholder, which is what rendered the empty "New food" form in #4681.
      if (!this.loadedFoodFromId) {
        this.cd.markForCheck();
        return this.auth.failPageGuardAsync(`Food '${id}' was not loaded before the page was shown`);
      }

      if (isRecipe(this.food)) {
        this.food.ingredients = this.food.ingredients.map(ingredient => {
          if (isIngredientReal(ingredient) && !ingredient.status) {
            ingredient.status = this.foodService.getFoodStatus(ingredient.foodId);
          }

          return ingredient;
        });
      }

      // disconnect from identity map — UI binds to draft, save merges back to canonical
      this.beginEditingDraft(this.food);

      // detect importable shared recipe: somebody else's, and not already imported
      this.isImportableRecipe = recipeShareRole(this.food) === RecipeShareRole.Importable;

      // detect imported recipe and lazy-check for updates
      this.isImportedRecipe = isRecipe(this.food) && !!this.food.local?.sourceRecipeId;
      if (this.isImportedRecipe) {
        void this.checkForOriginalUpdateAsync();
      }
    } else {
      if (mode === 'createFood') {
        this.setEditedFood(this.foodService.createNewFood(name, ean));
        // if label scan detected label style, override the setting for this session
        const scannedLabelStyle = this.navParams.data.local?.labelStyle;
        if (scannedLabelStyle === 'US') {
          this.settings.nutritionLabelStyle = NutritionLabelStyle.US;
        } else if (scannedLabelStyle === 'EU') {
          this.settings.nutritionLabelStyle = NutritionLabelStyle.EU;
        }

        // Give the new food a barcode, and note whether anything has to be asked first
        // (#4968). Two places can supply one and they can disagree — which one wins, and when
        // the user has to settle it, is decided by `barcode-pairing.ts`. The question itself
        // is raised once the page is on screen, in `ionViewDidEnter`, so setting the page up
        // is never held behind an alert.
        const pairing = decideBarcodePairing(
          this.navParams.data.local?.barcode,
          this.navParams.data.local?.scannedBarcode,
        );
        if (pairing.pairNow) {
          this.food.EANs = [pairing.pairNow];
        }
        this.pendingBarcodePairing = pairing;

        // merge pre-filled nutrients and measures from label scan
        if (this.navParams.data.local?.nutrients) {
          const nutrients = this.navParams.data.local.nutrients;
          const measures = this.navParams.data.local?.measures;
          this.labelScanConfidence = this.navParams.data.local?.labelScanConfidence;
          this.labelScanNutrientConfidences = this.navParams.data.local?.labelScanNutrientConfidences;
          this.labelScanJsonRepaired = this.navParams.data.local?.labelScanJsonRepaired;
          // nutrients from the LLM are per 100g, but the edit form expects values per
          // STD_AMOUNT. For US label style with a measure, STD_AMOUNT = the whole measure's
          // weight (see computeStandardAmount), so convert per-100g → per-serving. For EU
          // label style, STD_AMOUNT = 100 regardless of measures, so values stay as-is.
          const isUSServing = this.settings.nutritionLabelStyle === NutritionLabelStyle.US && measures?.length > 0;
          const measure = measures?.[0];
          const servingWeight = isUSServing ? measure.weight_g : 100;
          for (const tag in nutrients) {
            this.food.nutrients[tag] = nutrients[tag] * servingWeight / 100;
          }
          if (measures) {
            // rename "serving" to "portion" — "serving" is reserved as the implicit default in measures-list
            for (const m of measures) {
              if (m.name === 'serving') m.name = 'portion';
            }
            this.food.measures = measures;
          }
        }

        // pre-fill food name from label scan (Product Name, Brand)
        const { foodName, brandName, ingredients, description } = this.navParams.data.local || {};
        if (foodName || brandName) {
          this.food.name = foodName && brandName ? `${foodName}, ${brandName}` : (foodName || brandName);
        }

        // pre-fill image from label scan (upload in background)
        if (pendingLabelScanImageDataUrl) {
          const imageDataUrl = pendingLabelScanImageDataUrl;
          setPendingLabelScanImage(null);
          void this.uploadLabelScanImageAsync(imageDataUrl);
        }

        // pre-fill comments from label scan (description + ingredients)
        if (description || ingredients) {
          const parts: string[] = [];
          if (description) parts.push(description);
          if (ingredients) parts.push(`Ingredients: ${ingredients.replace(/^ingredients:\s*/i, '')}`);
          this.food.comments = parts.join('\n');
          this.commentsEditable = true;
        }
      } else if (mode === 'createRecipe') {
        this.setEditedFood(this.foodService.createNewRecipe());
      }
      this.isReadOnly = false;
    }

    // if food has no valid _id after loading (e.g. page recreated after app resume), exit
    if (!this.food._id) {
      void this.leaveUnusablePageAsync();
      return;
    }

    this.mode = mode;
    this.unmodifiedFood = deepClone(this.food);
    // Handle possible empty objects as guard for the view
    this.food.image = this.food.image || ({} as ScaledImage);
    this.food.nutrients = this.food.nutrients || {};
    this.nutrients = deepClone(this.food.nutrients);
    this.food.measures = this.food.measures || [];
    this.updateMeasuresHandler(this.food.measures);
    this.food.totalWeight_g = this.food.totalWeight_g || 0;
    this.updateTotalCarbs();
    this.displayNutrientsTotal = FormatNutrients(this.food.nutrients, this.userDb);
    this.updateDisplayNutrients();
    // Initialize macrosDisplayMode based on label style (before setNutritionLabelOrder)
    this.macrosDisplayMode = this.settings.nutritionLabelStyle === NutritionLabelStyle.US ? 'serving' : '100g';
    this.setNutritionLabelOrder(false); // don't reset since we just initialized
    this.foodSource = foodSourceInfoTable[getFoodSource(this.food._id)];
    this.foodSourceShortName = getFoodSourceShortName(this.foodSource.source);

    this.cd.markForCheck();

    // if this is an existing food (loaded from disk)
    if (this.food._rev) {
      // run the nutrient check immediately, so we can show the warning if needed
      this.checkValues = true;
    }

    // Ask before the page closes on an unsaved food (#5058). The page has no
    // Cancel button, so the back arrow, Android's hardware back button and a
    // swipe back were the only ways out — and all three threw the whole form
    // away without a word.
    //
    // Snapshot the form only NOW, after the normalisation above, for the reason
    // given on `foodAsLoaded`.
    //
    // The guard cannot watch `this.food` by reference: the page REPLACES that
    // object when a draft is committed on save, when publishing returns the
    // server's copy, and when the user duplicates the food. So it gets the
    // page's own comparison, which reads whatever `this.food` currently is.
    //
    // `getResult` hands the caller the saved food's id. It matters: an
    // ingredient that was pointing at an OFFICIAL food has to be repointed at
    // the private copy editing one creates, and `edit-ingredient`'s
    // `onFoodEdited` does that off this id. Without it, saving from the leave
    // dialog would write the copy and leave the ingredient on the old food, so
    // the user's edit would appear to have done nothing.
    //
    // It answers with the SAVED id, never `this.food._id` (#4968). The guard hands the result
    // over on its no-changes path too — the user may have saved already and be simply walking
    // out — and a food being CREATED carries an id from the moment the page builds it in
    // memory, long before anything is written. Answering with that id told the caller a food
    // existed when none did, and the food list now acts on such an answer by opening the
    // portion screen on it. Undefined until a save completes is the honest answer, and every
    // reader of this result already ignores a falsy id.
    this.foodAsLoaded = deepClone(this.food);
    //
    // A waiting page gets the answer its errand asked for (#5377): the meal screen reads a food
    // list result, everything else the bare id. `errandLeaveAnswer` gives Accept's answer.
    this.unsavedChangesGuard = this.unsavedChangesGuardProvider.createGuard<FoodsPageResult | string>(
      [],
      async () => { await this.saveFromLeaveDialogAsync(); },
      {
        hasUnsavedChanges: () => this.hasUnsavedFoodEdits(),
        getResult: () => errandLeaveAnswer(this.addFoodErrand, this.savedFoodId),
      },
    );
  }

  /**
   * Save on the way out, when the user picks Save in the leave dialog.
   *
   * The plain `save()` is not enough here. `cs-nav` runs the guard inside a
   * try/catch that turns any throw into "you may not leave" and a console line
   * nobody sees in production: no telemetry, no message, and a page that simply
   * refuses to close. So report the failure before letting it travel on. The
   * rethrow is what keeps the page open with the user's work still in it, which
   * is the right outcome — it just must not be silent. (#5056's review found
   * this same hole on the Meal Plan page.)
   */
  private async saveFromLeaveDialogAsync() {
    try {
      await this.save();
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.unsavedChanges.errors.saveFailed());
      throw err;
    }
  }

  /**
   * True when the form on screen differs from the food that was loaded.
   *
   * The two ignored fields are the same ones the publish path ignores: the
   * author fingerprint and the `local` block are bookkeeping the app writes for
   * itself, not edits the user made.
   */
  private hasUnsavedFoodEdits(): boolean {
    if (!this.foodAsLoaded) return false;
    return !deepCompareEQ(this.food, this.foodAsLoaded, { authorHash: true, local: true });
  }

  /**
   * Runs before the page closes, whichever way the user leaves: the back arrow,
   * Android's hardware back button, the browser back button or a swipe back.
   *
   * A food that is not yet fillable enough to save gets the Discard-only form of
   * the dialog, because offering Save on it would fail — that is what passing
   * the validation state does.
   */
  async ionViewCanLeave(): Promise<CanLeaveResult<FoodsPageResult | string>> {
    // Whether this leave needs a dialog at all is decided by mayLeaveWithoutAsking, which
    // carries the reasons. The latch is consumed here rather than in there because consuming
    // it is a one-way door and that helper is meant to be free of side effects.
    //
    // Three paths close the page themselves and arm that latch first: Accept (which has just
    // saved), Delete (which has just deleted), and the give-up path for a food that cannot be
    // shown. Each has already settled what happens to the edits.
    if (mayLeaveWithoutAsking({
      armedToLeaveWithoutAsking: this.leaveWithoutAsking.consume(),
      guardReady: !!this.unsavedChangesGuard,
      isReadOnly: this.isReadOnly,
      foodWasDeleted: this.foodWasDeleted,
    })) {
      return true;
    }
    const verdict = await this.unsavedChangesGuard.confirmLeaveAsync(this.isFoodValid());
    // a day errand has nobody waiting for the answer, so a food saved from the dialog still has
    // to open the portion screen -- see finishDayErrandOnLeave for why that refuses this leave
    return finishDayErrandOnLeave(this.navCtrl, this.addFoodErrand, verdict, this.savedFoodId,
      err => this.logging.trackAbnormalException(err));
  }

  /**
   * The errand this editor was opened with. It answers a waiting page with the bare id by default.
   * Read once and kept: reading reports a lost context, and one Back reads it twice.
   */
  private get addFoodErrand(): AddFoodErrand {
    this.addFoodErrandRead ??= readAddFoodErrand(this.navParams.data, ErrandAnswerShape.FoodId,
      (name, context) => this.logging.trackSilentProblem(name, context));
    return this.addFoodErrandRead;
  }
  private addFoodErrandRead: AddFoodErrand | undefined;

  /** Whether the form holds enough to be saved. Used by the Accept button and by
   *  the leave guard, which offers Discard alone when the answer is no. */
  private isFoodValid(): boolean {
    return !this.vldGroup
      || this.vldGroup.state === VldGroupState.Ready
      || this.vldGroup.state === VldGroupState.Done;
  }

  /**
   * Leave a page that has nothing to show, going back if there is anywhere to go back to and
   * Home if there is not.
   *
   * The `canGoBack` test is load-bearing, so please leave it in. This page really can be the
   * only one in the stack — opening its URL directly builds a stack of one page — and popping
   * the bottom of the stack does not go back: it either hangs and moves nothing, or walks the
   * user out of the app. `BrowserHistory.navigateBackAsync` in `browser-history.ts` explains
   * why. `send-feedback.ts` uses this same shape for the same reason.
   */
  private async leaveUnusablePageAsync() {
    // there is nothing on screen to lose, and nothing to ask about
    this.leaveWithoutAsking.set();
    if (this.navCtrl.canGoBack()) {
      await this.navCtrl.popAsync();
    } else {
      await this.navCtrl.setRootAsync(IHomePage, {});
    }
  }

  /**
   * Close this editor because the food it was editing has been deleted, unless something already
   * is (df-32c03324a817). Claims `editorCloseStarted` first — that field's comment says why the
   * two closers must not both run — and waits for any back navigation still in progress.
   */
  private async closeDeletedFoodEditorAsync() {
    if (this.editorCloseStarted) return;
    this.editorCloseStarted = true;
    // if a back navigation is still being handled, closing has to wait for it to finish.
    // popAsync refuses outright while the popstate handler runs -- it reads a second pop as a
    // double-tap on the pop already in flight and returns having done nothing at all. That is
    // exactly where ionViewDidEnter runs, and a back is the usual way the user arrives at a page
    // buried under another one, so without this the close is silently swallowed on the one path
    // this whole guard exists for. Queue it for the moment the handler finishes instead; that is
    // what finishDayErrandOnLeave in pages/foods/add-food-errand.ts does with the same problem.
    //
    // The .catch is required, not tidiness. The queue's runner wraps each callback in a
    // try/catch that only sees a SYNCHRONOUS throw, so a rejection from an async thunk escapes
    // it and reaches nobody -- that is df-d35ba5ca8f2f, a production crash on this exact queue,
    // and finishDayErrandOnLeave catches for the same reason. It is also the only thing covering
    // the app tearing down between the queueing and the flush: NavController.getActive reads
    // this.nav.pages with no guard of its own, and the nav component may be gone by then.
    if (this.navCtrl.isBrowserNavigationInProgress()) {
      this.navCtrl.queuePostBrowserNavCallback(() => {
        this.leaveDeletedFoodEditorAsync().catch(err => this.logging.trackAbnormalException(err));
      });
      return;
    }
    await this.leaveDeletedFoodEditorAsync();
  }

  /**
   * Actually close, and give the claim back if the page is still here afterwards.
   *
   * A close is not a guarantee that the page went away: `navCtrl.popAsync`'s own comment says
   * `cs-nav` puts this page back when the ENTERING page refuses to be entered. The latch is only
   * there to stop a second navigation starting while one is in flight, so a page that survived
   * the attempt must be free to try again on the next entry — holding the claim would leave the
   * editor for a deleted food open for good, which is the thing this all exists to prevent.
   */
  private async leaveDeletedFoodEditorAsync() {
    // close this page only while it is still the page on top. popAsync closes whatever IS on
    // top, not whoever asked, so a close that has waited for a back navigation to finish must
    // ask again before it fires -- the stack may have moved in the meantime, by the very back
    // the close was waiting for, or by another callback flushed ahead of this one (the queue
    // exists mostly for deferred pushes). Popping then would take away the screen the user had
    // just arrived at, which is the exact harm deleteFoodAsync's viewDestroyed guard prevents.
    // Standing down is safe: this page is left with foodWasDeleted set, so it still refuses
    // every write, and the latch is given back below so the next entry closes it.
    //
    // A page that has LEFT is the same stand-down, answered without asking the nav stack -- and
    // it gives the claim back for the same reason. `viewDestroyed` means "this view has left",
    // not "this component is gone": a page a push merely COVERED has it set and is still alive
    // and still on the stack, so it can be entered again and must be free to close itself then.
    // Keeping the claim here is how the self-close gets disabled for good, which is the one
    // outcome this method exists to prevent.
    if (this.viewDestroyed || this.navCtrl.getActive()?.instance !== this) {
      this.editorCloseStarted = false;
      return;
    }
    try {
      await this.leaveUnusablePageAsync();
    } finally {
      // a page that really left got _navPrepareLeave on the way out, which is what sets this
      if (!this.viewDestroyed) this.editorCloseStarted = false;
    }
  }

  /**
   * Whether this page must refuse to write the food, because it has already deleted it
   * (df-32c03324a817). Reports every refusal: the user can do nothing about it, but a future
   * debugger needs to know a write was attempted against a deleted food rather than wondering
   * why one came back.
   *
   * EVERY action on this page that writes the food asks this first, not only Save
   * (df-e3f98d757250): the delete leaves the page usable for as long as the unpublish keeps
   * retrying, and voting, sharing, unsharing and refreshing from the original all end in a write
   * of their own. A new action that writes `this.food` belongs on that list too.
   */
  private mustNotWriteDeletedFood(): boolean {
    if (!this.foodWasDeleted) return false;
    this.logging.trackSilentProblem('edit-food.save-after-delete', { foodId: this.food?._id });
    return true;
  }

  getDisplayedNutrients() {
    // use macrosDisplayMode to determine which nutrients to display
    if (this.macrosDisplayMode === 'serving') {
      return this.displayNutrientsPerServing;
    }

    return this.displayNutrientsPer100g;
  }

  handleSwipeForward() {
    if (this.selectedRecipeTab === RecipeTab.Overview) {
      this.selectedRecipeTab = RecipeTab.Foods;
      this.cd.markForCheck();
    }
  }

  handleSwipeBack() {
    if (this.selectedRecipeTab === RecipeTab.Foods) {
      this.selectedRecipeTab = RecipeTab.Overview;
      this.cd.markForCheck();
    }
  }

  ngAfterViewInit() {
    // enable tab content animation after initial render
    requestAnimationFrame(() => {
      this.animateTabContent = true;
      this.cd.markForCheck();
    });
  }

  async save() {
    // refuse to write a food this page has already deleted, rather than resurrecting it
    // (df-32c03324a817). The helper reports the attempt.
    if (this.mustNotWriteDeletedFood()) {
      return;
    }
    // if editing an identity-mapped document, the draft is merged back to canonical before the
    // save and a fresh draft is opened after it, even when it throws (internal #727)
    await saveThroughDraftAsync<Food | Recipe>({
      getHolder: () => this.foodDraft,
      getFood: () => this.food,
      // assigned directly on purpose: the holder stays, still pointing at the same document
      showCommitted: canonical => { this.food = canonical; },
      beginEditingDraft: canonical => {
        this.beginEditingDraft(canonical);
        this.cd.markForCheck();
      },
    }, () => this.saveCommittedFoodAsync());
  }

  /** The body of save(), run on the canonical document once any draft has been merged into it. */
  private async saveCommittedFoodAsync() {
    this.updateTotalCarbs();
    // the grams the inputs stand for, taken now, while the measures are still the ones the
    // hint above the inputs named when Save was pressed -- the clean-up below can drop the
    // first of them and so move the getter's answer (#6024)
    const standardAmount = this.STD_AMOUNT;

    let customMeasures = await this.userDb.loadCustomMeasuresAsync();
    // whether the user's reusable measure-NAME list gained a name below. It gates nothing but
    // the write of that list, so adding a measure to the FOOD must not set it (#4722) -- doing
    // so saved a new revision of the name-list document with nothing in it changed.
    let customMeasureNamesChanged = false;

    // Removes measures without any name or without gram weight
    this.food.measures = this.food.measures.filter(measure => measure.name);
    this.food.measures = this.food.measures.filter(measure => measure.name === RECIPE_SERVINGS_MEASURE_NAME || measure.weight_g > 0);
    if (this.food.measures.length > 0) {
      this.food.measures.forEach(measure => {
        // a name the app writes for itself — 'prepared' (a recipe's servings carrier) or
        // 'serving' (the US label's serving size) — is not a user-defined serving size, so it
        // must not be registered into the custom-measure name list that feeds the measure-name
        // picker for every food. Registering 'serving' is what made it choosable, and choosing
        // it made the user's own row disappear (#4722).
        if (isReservedMeasureName(measure.name)) return;
        if (!customMeasures.names.includes(measure.name)) {
          customMeasures.names.push(measure.name);
          customMeasureNamesChanged = true;
        }
      });
    }

    if (customMeasureNamesChanged) {
      const transactionId = DatabaseWrapper.getTransactionId();
      await this.userDb.saveUserMeasuresAsync(transactionId);
    }

    await this.updateAsync(standardAmount);

    // The food is now in the database, so it is answerable. Nothing before this line is:
    // creating a food only builds an object in memory with an id already on it, and handing
    // that id to a caller who then opens the portion screen on it means opening it on a food
    // that does not exist (#4968). Recorded here rather than inferred from `mode`, because
    // this is the one place that knows a save actually completed.
    this.savedFoodId = this.food._id;
  }

  // Note: Callbacks are now handled via the returnCallback mechanism in NavController.
  // Parent pages should use returnCallback param instead of passing callback functions.
  // The food ID is passed to popAsync() when saving successfully.

  handleVote(direction: -1 | 1) {
    void this.voteAsync(direction);
  }

  async voteAsync(direction: -1 | 1) {
    // this page has already deleted the food it was editing, so voting on it would write a new
    // revision of the deleted document and bring the food back (df-e3f98d757250). Close the
    // editor rather than doing nothing at all, the way handleAcceptButton does: the delete
    // leaves this page usable for as long as the unpublish keeps retrying, so a tap that
    // silently did nothing would read as a broken button. The helper reports the attempt.
    if (this.mustNotWriteDeletedFood()) {
      await this.closeDeletedFoodEditorAsync();
      return null;
    }
    if (this.food) {
      this.food.local = this.food.local || {};
      this.foodService.voteFood(this.food, direction);
      let saving = this.foodService.saveUserFoodAsync(this.food, direction === -1 ? 'cached' : 'used');
      return saving; // so someone can wait if needed somewhere
    }

    return null;
  }

  updateDisplayNutrients() {
    this.displayNutrientsPer100g = deepClone(this.displayNutrientsTotal);
    if (this.food.totalWeight_g) {
      const nutrientsPer100g = scaleNutrientsToPer100g(this.food.nutrients, this.food.totalWeight_g);
      this.displayNutrientsPer100g = FormatNutrients(nutrientsPer100g, this.userDb);
    }

    this.displayNutrientsPerServing = deepClone(this.displayNutrientsPer100g);

    // for recipes, "serving" = total recipe, so use total nutrients
    if (isRecipe(this.food)) {
      this.displayNutrientsPerServing = deepClone(this.displayNutrientsTotal);
    } else {
      const servingSizeMeasure = findServingMeasure(this.food.measures);
      if (servingSizeMeasure && servingSizeMeasure.weight_g) {
        const nutrientsPerServing = scaleNutrientsToServing(this.food.nutrients, servingSizeMeasure);
        this.displayNutrientsPerServing = FormatNutrients(nutrientsPerServing, this.userDb);
      }
    }

    // CarbTot (CHOCDF_g) is a display-only US label field — never saved to food.nutrients.
    // Synthesize it from CarbNet + Fiber so the read-only Nutrition Facts table shows a value.
    synthesizeCarbTotDisplay(this.displayNutrientsPer100g);
    synthesizeCarbTotDisplay(this.displayNutrientsPerServing);

    this.cd.markForCheck();
  }

  updateTotalNutrientsHandler(nutrients: Nutrients) {
    this.food.nutrients = nutrients;
    this.displayNutrientsTotal = FormatNutrients(nutrients, this.userDb);
    this.updateDisplayNutrients();
    this.cd.markForCheck();
  }

  updateTotalWeightHandler(weight_g: number) {
    this.food.totalWeight_g = weight_g;
    this.cd.markForCheck();
  }

  updateMeasuresHandler(measures: FoodMeasure[]) {
    if (this.isRecipe()) {
      // a recipe uses one single measure, whose `amount` is the servings the recipe yields
      // (per-serving weight = weight_g / amount). resolveRecipeMeasures owns the whole
      // decision, including not emptying a non-empty list (#4380) -- its one exception is a
      // measure the editor hides from the user, which it skips rather than adopts.
      const resolved = resolveRecipeMeasures(measures, this.food.totalWeight_g);
      this.food.measures = resolved.measures;
      if (resolved.didAdoptStrayMeasure) {
        // the repair has already been applied by the line above, so nobody needs to act --
        // this is the info tier, recorded only so a future debugger can see it happened.
        this.logging.trackSilentInfo('recipe-measures.adopted-stray-measure', {
          foodId: this.food._id,
          servingsKept: resolved.measures[0]?.amount,
        });
      }
    } else {
      // a food that is not a recipe may not carry the recipe servings measure, so it is taken
      // off here. Nothing offers that name any more, so a food that still has one was saved
      // before the name was reserved -- which makes this a repair on the user's stored data,
      // and the user is never told it happened. So report it either way.
      const stripped = stripRecipeServingsMeasure(measures);
      this.food.measures = stripped.measures;
      if (stripped.removedMeasures.length > 0) {
        this.reportStrippedRecipeMeasures(stripped.removedMeasures, stripped.measures.length);
      }
    }
    this.cd.markForCheck();
  }

  // Reports that a plain food carried the recipe servings measure and that it has just been
  // removed. Which tier this belongs to depends on what the food is left with, so both are here
  // side by side rather than at the call site:
  //
  // - other measures remain: the repair has already been applied by the caller and the user can
  //   still see a serving size for this food, so nothing is wrong -- info tier.
  // - nothing remains: the food's ONLY serving size has just gone, and the user is not told.
  //   That is the condition this whole report exists for -- problem tier.
  private reportStrippedRecipeMeasures(removedMeasures: FoodMeasure[], remainingCount: number) {
    const context = {
      foodId: this.food._id,
      removedCount: removedMeasures.length,
      // the numbers the user had typed into the measure, so a debugger can see what was lost
      removedAmount: removedMeasures[0]?.amount,
      removedWeight_g: removedMeasures[0]?.weight_g,
      remainingCount,
    };
    if (remainingCount > 0) {
      this.logging.trackSilentInfo('food-measures.removed-recipe-measure', context);
    } else {
      this.logging.trackSilentProblem('food-measures.removed-the-foods-only-measure', context);
    }
  }

  updateIngredientsHandler(ingredients: Ingredient[]) {
    if (isRecipe(this.food)) {
      this.food.ingredients = ingredients;
    }
    this.cd.markForCheck();
  }

  updateInstructionsHandler(instructions: string) {
    if (isRecipe(this.food)) {
      this.food.instructions = instructions;
    }
    this.cd.markForCheck();
  }

  updateImageHandler(image: ScaledImage) {
    this.food.image = image;
    this.cd.markForCheck();
  }

  private async uploadLabelScanImageAsync(dataUrl: string) {
    console.log('Label scan image upload starting, dataUrl length:', dataUrl.length);
    try {
      const scaledImage = await this.imageService.processDataUrlAsync(dataUrl, 'food-images', true);
      console.log('Label scan image upload complete, sizes:', scaledImage.sizes?.length, scaledImage.sizes?.map(s => `${s.width}x${s.height}`));
      if (this.viewDestroyed) return;
      this.food.image = scaledImage;
      this.cd.markForCheck();
    } catch (err) {
      // this is a background best-effort upload, so don't block food creation
      // with an alert — but the failure must reach telemetry instead of being
      // swallowed, so we know how often label-scan photos fail to save.
      this.logging.trackSilentProblem('LabelScanImageUploadFailed', { error: describeError(err) });
    }
  }

  async handleAcceptButton() {
    // the food this page was editing has been deleted, so there is nothing to accept and nothing
    // to hand back to whoever is waiting for an answer -- returning the id of a deleted food is
    // what #4968 was about. Close the editor instead of doing nothing at all: the delete leaves
    // this page on screen and interactive for as long as the unpublish keeps retrying, so a tap
    // that silently did nothing would read as a broken button. The helper reports the attempt.
    if (this.mustNotWriteDeletedFood()) {
      await this.closeDeletedFoodEditorAsync();
      return;
    }
    if (!this.checkValues) {
      this.checkValues = true;
    }
    // vldGroup may be undefined if inside *ngIf that wasn't rendered
    this.vldGroup?.onNextClicked(null);
    if (!this.isFoodValid()) {
      return;
    }
    let warning = this.caloriesConsistencyWarning();
    if (warning) {
      let acceptAnyway = await this.alertCtrl.showAlertAsync({
        title: LL.editFood.alerts.inconsistentValues(),
        buttons: [
          { text: LL.editFood.buttons.useAnyway(), role: AlertButtonRole.Primary, result: true },
          { text: LL.editFood.buttons.doubleCheck(), role: AlertButtonRole.Cancel, result: false },
        ],
      });
      if (!acceptAnyway) {
        return;
      }
    }

    let loading = this.loadingCtrl.create();
    void loading.present();
    // the dismiss must be in a finally: a save that throws used to leave the spinner on screen
    // for good, which is the "loop loading" the customer in #4681 reported after the page had
    // failed to load. The error still propagates — this only guarantees the spinner goes away.
    try {
      await this.save();
      // Finish the errand the user is on (#5377): answer the waiting page, or open the portion
      // screen for a day in this page's place, or just close. The leave guard is skipped only on
      // a close -- the save just happened, so it has nothing left to ask about.
      await answerAddFoodErrandAsync(this.navCtrl, this.addFoodErrand, this.food._id,
        ErrandAnswerer.FoodEditor, () => this.leaveWithoutAsking.set());
    } finally {
      void loading.dismiss();
    }
  }

  setReadonly(enabled: boolean) {
    this.isReadOnly = enabled;
    this.cd.markForCheck();
  }

  showImageCard(): boolean {
    return !this.isReadOnly || this.hasImage();
  }

  hasImage(): boolean {
    return this.food.image && this.food.image.sizes && this.food.image.sizes.length > 0;
  }

  /**
   * The staple's own picture, shown where a food's photo would be when it has none (staple pictures
   * plan, #5694), or `undefined` for exactly today's page.
   *
   * Nothing needs to be awaited here: loading the food already built the staple snapshot (a staple id
   * is answered through `stapleDb.getStapleByIdAsync`), and the loaded document is the curated one,
   * so its own `staple.picture` is current and available at the first render.
   */
  staplePicture(): StaplePictureFiles | undefined {
    const files = staplePictureFor(this.food, {
      pictureNameForId: (foodId: string) => this.stapleDb.pictureNameForId(foodId),
    });
    return files === undefined || this.staplePictureFailed ? undefined : files;
  }

  /**
   * The staple picture could not be loaded — offline on the web, where only the small pictures are
   * pre-cached. The page then looks exactly as it did before this feature, rather than showing a
   * broken image.
   */
  handleStaplePictureError(): void {
    this.staplePictureFailed = true;
    this.cd.markForCheck();
  }

  async handleDeleteOption() {
    let options: AlertOptions<boolean> = {
      message: LL.editFood.alerts.confirmDelete({
        type: this.isRecipe() ? LL.editFood.types.recipe() : LL.editFood.types.food()
      }),
      enableBackdropDismiss: true,
      buttons: [],
    };

    options.buttons.push({
      text: LL.common.buttons.delete(),
      role: AlertButtonRole.Primary,
      result: true,
    } as AlertButton<boolean>);
    options.buttons.push({
      text: LL.common.buttons.cancel(),
      role: AlertButtonRole.Cancel,
    });

    if (await this.alertCtrl.showAlertAsync(options)) {
      await this.deleteFoodAsync();
    }
  }

  getLabelStyleOptionText() {
    return LL.editFood.labels.labelStyle() + ': ' + (this.settings.nutritionLabelStyle === NutritionLabelStyle.US ? 'US' : 'EU');
  }

  handleLabelOptionButtonClick() {
    if (this.settings.nutritionLabelStyle === NutritionLabelStyle.US) {
      this.settings.nutritionLabelStyle = NutritionLabelStyle.EU;
    } else {
      this.settings.nutritionLabelStyle = NutritionLabelStyle.US;
    }
    this.setNutritionLabelOrder();
    this.cd.markForCheck();
  }

  async deleteFoodAsync() {
    // use canonical reference (not draft clone) to avoid identity map bypass
    const food = this.foodDraft?.canonical || this.food;
    // The local delete has to succeed before ANY of the rest of this method may run. Everything
    // below assumes the food is gone: the unpublish would take a food the user still has out of
    // the global database, the food-updated event would tell other screens it changed, and the
    // pop would close the editor as if the delete had worked. So a failure stops here, says so,
    // and changes nothing (df-02c50cb0f1fa). This is the same "an error nobody hears about"
    // shape that `4fa7aab11` fixed for the unpublish below, for the local half of the delete.
    try {
      await this.userDb.deleteFoodAsync(food, DatabaseWrapper.getTransactionId());
    } catch (err) {
      // Nothing to undo on the document itself: `deleteRecordAsync` marks it deleted before it
      // tries to save it, and since df-1cb1333d7899 it puts that mark back the way it found it
      // when the save throws. This page used to clear the flag here, for every holder of the
      // shared document rather than just itself, which is the job that moved into the wrapper.
      this.logging.trackAbnormalException(err, LL.errors.userFacing.foodDeleteFailed());
      return;
    }
    // the local delete is through, so from here on this page is editing a food that is gone.
    // Set BEFORE the unpublish await, not after it: the whole point is to cover the long window
    // the unpublish opens, and a flag set at the end of that window covers none of it.
    this.foodWasDeleted = true;
    // legacy meals (food/+UDM or meal/ prefixes) were never published to server, so skip unpublish
    if (!isLegacyMealId(food._id)) {
      await this.unpublishDeletedFoodAsync(food);
    }
    this.pubSub.publish(FoodUpdatedEvent, {});
    // the food is gone, so asking whether to save edits to it would be absurd
    this.leaveWithoutAsking.set();
    // Answer with NOTHING, so a food id coming back from this page means "saved" and only that
    // (#4968). Deleting used to hand the id back too, which was indistinguishable from a save.
    // That was harmless while the food list ignored the answer for a created food; now that it
    // acts on it — by opening the portion screen on the food — an id returned by a DELETE would
    // ask the user how much of a food they just destroyed they would like to log.
    //
    // Nothing wanted the id here. The two pages that read this answer are the food list, which
    // is the case above, and the portion screen, which compares the id against the ingredient's
    // own to notice a private copy being made and does nothing when they match.
    //
    // if the user already left this screen while the unpublish was retrying, do not pop --
    // popAsync closes whatever page is on top, which by then is not this one, so popping
    // would take away the screen they moved on to. That window is long: unpublishFood is a
    // resilient callable, so a failing one sleeps 1/2/4/8/15 s between six attempts that
    // each get 80 s before they time out -- about 30 s when the server answers promptly and
    // up to ~8.5 minutes when the network hangs (cloud-functions-retry.ts's header comment).
    //
    // Closing goes through the shared closer rather than a bare popAsync, for two reasons. It
    // claims editorCloseStarted, which covers the gap viewDestroyed cannot: the user may have come
    // BACK by now, which clears viewDestroyed before ionViewDidEnter has had its chance to close
    // this page, and then both would close and take the screen underneath with them. And it tests
    // canGoBack, which a bare pop does not -- this editor really can be the only page in the
    // stack, and popping the bottom of the stack moves nothing.
    if (this.viewDestroyed) return;
    await this.closeDeletedFoodEditorAsync();
  }

  /**
   * Ask the server to take a just-deleted food out of the global, searchable food
   * database, and make sure a failure to do so cannot pass unnoticed (df-274672bb1f5d).
   *
   * Two things this method exists for, both of which the inline version got wrong:
   *
   * - It NEVER throws. `unpublishFoodAsync` is registered with the resilient retry
   *   policy, so it throws once its retries are exhausted. Inline and unguarded, that
   *   threw straight out of `deleteFoodAsync` after the local delete had already run —
   *   so the food-updated event never fired, the editor never closed, and the leave
   *   guard then offered to save the food the user had just deleted.
   * - It reports the ONE failure that is a real problem, and stays quiet about the
   *   ordinary case. A food that was never published answers with an error too, and
   *   that is the common path — every unshared recipe, every brand-new food deleted
   *   before the publish sweep ran, and every just-duplicated copy. See
   *   `unpublish-outcome.ts` for how the two are told apart.
   *
   * A third thing was added later (df-efc66c4a2f6e): when the request fails for a food
   * we believe IS in the global database, the removal is written into the food unpublish
   * outbox and replayed when the connection returns. Before that, the ask happened once
   * and a food deleted while offline stayed published for ever, because the local copy
   * that could have carried the obligation had just been deleted.
   */
  private async unpublishDeletedFoodAsync(food: Food) {
    let failure: string | undefined;
    let couldNotReachServer = false;
    try {
      const result = await this.cloudFunctionsService.unpublishFoodAsync({ food, adminMode: false });
      failure = result.error;
    } catch (err) {
      failure = describeError(err);
      couldNotReachServer = isTransportFailureAfterRetries(err);
    }

    const outcome = describeUnpublishOutcome(food, failure !== undefined);
    if (outcome === UnpublishOutcome.Removed) {
      console.log(`Food ${food._id} successfully unpublished.`);
      return;
    }
    if (outcome === UnpublishOutcome.NothingToRemove) {
      // this user's own publish never put the food in the global database, so there was
      // nothing there for us to take out and the end state is the one we wanted. Not worth a
      // report: this is the ordinary answer for a food that was never published, and for the
      // cached copy of somebody else's food.
      console.log(`Food ${food._id} was not published by this user, so there was nothing to unpublish - ${failure}`);
      return;
    }
    // the food is still in the global database, searchable by everybody, and the local copy
    // this delete removed was the only thing that would ever have asked again -- so write the
    // obligation down somewhere that outlives this page, and let the outbox ask again when the
    // connection comes back (df-efc66c4a2f6e). Awaited, but it never throws: a failure to
    // remember is reported inside and must not stop the delete from finishing.
    await this.foodUnpublishOutbox.enqueueAsync(food, isRecipe(food));
    // The report stays, and is still at the silent-problem tier. The queued retry does not
    // make the condition resolved: until a drain succeeds the food IS in the global database,
    // and it may never succeed. Nothing here is for the user to act on, so no message.
    //
    // A transport failure is reported here as well as by the cloud-functions wrapper,
    // because the two say different things: the wrapper reports that a call failed, and this
    // reports that a food was left published until something replays the removal.
    this.logging.trackSilentProblem('food-unpublish.failed-while-published', {
      foodId: food._id,
      isRecipe: isRecipe(food),
      shared: !!food.local?.shared,
      couldNotReachServer,
      error: failure,
    });
  }

  // used to show the warning if calories seem inconsistent with macros
  caloriesConsistencyWarning(): TranslatedString | undefined {
    // return early if food or nutrients are not initialized
    if (!this.food || !this.food.nutrients) {
      return undefined;
    }

    // don't show the warning until a required non-calorie nutrient is entered
    // (so that we don't bother someone who enters just calories) — matches the
    // sibling guards in ingredient-list.ts and edit-ingredient.ts
    if (!NutrientDefinitionsOrdered.some(d => d.required && isNutrientValueEntered(this.food.nutrients[d.tag]) && d.tag !== INFOODS.Calories)) {
      return undefined;
    }

    let { correct, expectedCalories } = checkNutrientCalories(this.food.nutrients);
    if (!correct) {
      return LL.editFood.errors.caloriesInconsistent({ expectedCalories: expectedCalories.toFixed(0) });
    }
    return undefined;
  }

  // used by [validity] property in vld-error component in the form
  validity(field: string) {
    if (field === 'name') {
      if (!this.food.name || this.food.name.length < 3) {
        return LL.editFood.errors.nameRequired();
      }
    }

    if (isRecipe(this.food)) {
      if (field === 'ingredients') {
        if (this.food.ingredients.length === 0) {
          return LL.editFood.errors.ingredientRequired();
        }
      }
    } else {
      if (field == 'per') {
        if (!this.STD_AMOUNT) {
          return LL.editFood.errors.portionRequired();
        }
      }

      if (field.startsWith('nutrient/')) {
        let tag = field.split('/')[1];
        if (tag === INFOODS.KiloJoules) { // kJ is a special case for EU labels-only, not a real display nutrient
          tag = INFOODS.Calories;
        }

        let display = this.displayNutrientsTotal.find(d => d.tag === tag);
        let isRequired = display && display.required;
        if (tag === INFOODS.CarbTot) { // in the US label, we should show error under total carbs, even though internally use only net carbs
          isRequired = true;
        }

        if (isRequired && !isNutrientValueEntered(this.food.nutrients[tag])) {
          return LL.editFood.errors.nutrientMissing();
        }
      }
    }

    return null;
  }

  nutrientInputChanged(newValue: number, nutrientTag: string) {
    void newValue;
    void nutrientTag;
    //this.nutrientsPer100g[nutrientTag] = (newValue / this.STD_AMOUNT) * 100;
    this.cd.markForCheck();
  }

  /** Stores the form's values into the food and writes it; `standardAmount` is the grams the inputs stand for. */
  async updateAsync(standardAmount: number) {
    // for regular foods, convert from display amount to per-100g and update food.nutrients
    // for recipes, nutrients are already set by updateTotalNutrientsHandler (from ingredient-list)
    if (!isRecipe(this.food)) {
      storeEditedNutrientsAsPer100g(this.food.nutrients, this.nutrients, standardAmount);
    }

    // CarbTot is only used for US label display, never saved
    delete this.food.nutrients[INFOODS.CarbTot];

    // legacy meals and unshared recipes are only saved locally, not published to server
    if (isLegacyMealId(this.food._id) || (isRecipe(this.food) && !this.food.local?.shared)) {
      // Awaited, not dropped (#4968). `save()` records the food as answerable once this
      // returns, and the caller may then hand its id straight to a screen that reads the food
      // back by id -- which finds nothing if the write is still in flight. Awaiting also means
      // a rejected write reaches the caller instead of only the global unhandled-rejection path.
      await this.foodService.saveUserFoodAsync(this.food, 'used');
      this.pubSub.publish(FoodUpdatedEvent, {});
      return;
    }

    // a shared recipe may have gained ingredients since it was shared. Publish those first,
    // or everybody who opens the share sees a blank row for each of them (#3255)
    if (isRecipe(this.food)) {
      const failedIngredientCount = await this.publishMissingIngredientsAsync(this.food);
      if (failedIngredientCount > 0 && !this.viewDestroyed) {
        this.toastController.showToast({
          message: LL.editFood.buttons.shareIngredientsFailed(),
          duration: 4000,
        });
      }
    }

    let result: PublishFoodResults;
    // the document this page was editing when publishing began. Publishing can move the
    // page onto a DIFFERENT document in two ways below -- the server answers with its own
    // copy under a new id, and the local fallback mints a fresh user-food id in place --
    // and the draft holder has to be dropped when it does. Compared once, below both of
    // those sites and above the write -- so a future id change added anywhere between
    // here and that comparison is covered without anybody remembering to add a line.
    const idBeforePublish = this.food._id;
    console.log('Created/Updated food:', this.food);
    try {
      result = await this.cloudFunctionsService.publishFoodAsync({ food: this.food, adminMode: false });
    } catch (e) {
      result = { error: `${e}\n${deepCheckValid(this.food, 'food')}` };
      this.logging.trackAbnormalError(result.error);
    }
    if (result.food) {
      // If publising had succeded mark food as our private as it means we are edited our owned copy (because publishing always works on our owned copy of a food)
      result.food.status = FoodStatus.Private;
      // If our owned copy of published food is created on server
      if (result.food._id !== this.food._id) {
        // Remove original food (from which published copy is based) from user database as we want newly created copy to be displayed and used by user.
        const oldFood = this.food;
        // the server clears `local` on every write, so carry over what this device kept about
        // the food -- a shared recipe must stay shared (df-18a753fad787)
        result.food.local = localBlockForForkedCopy(oldFood);
        this.setEditedFood(result.food);
        // reported and swallowed by the helper: publishing has already succeeded and the page
        // is on the server's copy, so a throw here would skip the save below
        await this.removeSupersededFoodAsync(oldFood, result.food._id);
      }
    }
    // This should only happen if we didn't manage to publish our own copy of the food for some reaseon (e.g. network issue)
    // In that case we will create our own copy localy which will be published later.
    if (
      this.food.status !== FoodStatus.Private &&
      !deepCompareEQ(this.food, this.unmodifiedFood, { authorHash: true, local: true })
    ) {
      this.food._rev = undefined;
      this.food.authorHash = undefined;
      const foodTypeId = isRecipe(this.food) ? 'UDR' : 'UDF';
      generateUserFoodId(this.auth.user.uid, this.food, foodTypeId, crypto);
      this.food.status = FoodStatus.Private;
    }
    // if either branch above moved the page onto a different document, the draft holder no
    // longer has anything to merge back into, so drop it. `commitDraft()` answers with the
    // canonical, which keeps its OWN `_id` -- so a holder left standing here would make a
    // SECOND save on this page write the pre-publish document back over the one just
    // saved, under the pre-publish id, throwing away every edit made since and saying
    // nothing. That is #5086 exactly, at the two sites its fix did not reach; the third,
    // `handleDuplicateOption`, clears the holder for the same reason.
    //
    // This sits BEFORE the write below, and that placement is the whole point. The way
    // this bug is actually reached is a save that throws AFTER the id changed, leaving the
    // page open for the user to tap Accept again -- and the awaited write below is what
    // throws. Clearing after it would miss every case that matters.
    if (this.food._id !== idBeforePublish) {
      this.foodDraft = undefined;
    }
    // awaited for the same reason as the local-only branch above (#4968)
    await this.foodService.saveUserFoodAsync(this.food, 'used');
    // Publishing just wrote the food, and may have swapped `this.food` for the
    // server's copy of it. Move the leave guard's baseline onto whatever is on
    // screen now, or the guard would report edits the user has already saved
    // and ask about them on the way out (#5058).
    this.foodAsLoaded = deepClone(this.food);

    if (result.error) {
      console.error(`Can't publish food ${this.food._id} - ${result.error}`);
    } else if (result.food) {
      console.log(`Food ${this.food._id} successfully published.`);
    }
    // this is needed to update view on home page if returning from this page
    this.pubSub.publish(FoodUpdatedEvent, {});
  }

  /**
   * Remove the user's copy of a food that the server's copy under a new id now replaces. This
   * is the only thing that removes it, so a failure leaves the user with TWO entries for the
   * same food and no way to tell which is which. It has to be reported explicitly, because a
   * revision conflict is filtered out of Sentry on purpose (SentryErrorHandler.handleError).
   *
   * Reported and then swallowed: publishing has already succeeded and the page is on the
   * server's copy. No user-facing message either -- the user's save worked and they can delete
   * the duplicate themselves, so it is the silent-problem tier.
   */
  private async removeSupersededFoodAsync(oldFood: Food, publishedFoodId: string) {
    try {
      await this.userDb.deleteFoodAsync(oldFood, DatabaseWrapper.getTransactionId());
    } catch (err) {
      // ids go in the context, not the name, so repeats collapse into one Sentry issue
      this.logging.trackSilentProblem('food-publish.superseded-copy-delete-failed', {
        foodId: oldFood._id,
        publishedFoodId,
        isRecipe: isRecipe(oldFood),
        error: describeError(err),
      });
    }
  }

  /**
   * If the first share of this recipe came back as a copy under a new id, make that copy the
   * user's recipe and put it on this page, so the link shared next has something of theirs
   * behind it (df-0497c91194af). It happens when the recipe was unshared before the server
   * started remembering who unshared it. A save that fails throws, and the old recipe stays.
   */
  private async adoptSharedRecipeCopyIfForkedAsync(result: PublishFoodResults) {
    const previousId = this.food._id;
    const copy = await adoptSharedRecipeCopyAsync(this.food, result.food, {
      saveCopyAsync: async (food) => {
        try {
          await this.foodService.saveUserFoodAsync(food, 'used');
        } catch (err) {
          // the copy is already public under its new id, and nothing on the device names it any
          // more, so this report is the only way anybody finds it again. Rethrown so the share
          // is reported to the user as failed, and the old recipe is left as it was
          this.logging.trackSilentProblem('recipe-share.published-copy-not-saved', {
            previousId,
            publishedId: food._id,
            error: describeError(err),
          });
          throw err;
        }
      },
      removeSupersededAsync: (oldFood) => this.removeSupersededFoodAsync(oldFood, result.food!._id),
    });
    // if the server kept the id, the recipe on this page is the shared one
    if (!copy) {
      return;
    }
    this.setEditedFood(copy);
    // the leave guard's baseline follows the page onto the copy, as in updateAsync (#5058)
    this.foodAsLoaded = deepClone(copy);
    this.logging.trackSilentInfo('recipe-share.published-as-new-copy', {
      previousId,
      publishedId: copy._id,
      forkReason: result.forkReason,
    });
    this.pubSub.publish(FoodUpdatedEvent, {});
  }

  somethingChanged() {
    // error messages are only checked once at least something is entered
    this.checkValues = true;
    this.vldGroup?.onNextClicked(null);
    this.cd.markForCheck();
  }

  handleDuplicateOption() {
    // the copy is a brand-new document, so setEditedFood() drops the draft holder (#5086)
    this.setEditedFood(this.foodService.duplicateFood(this.food));
    this.setReadonly(false);
    this.cd.markForCheck();
  }

  /**
   * Put a food on screen that is NOT a draft of a shared database document: a freshly
   * created or duplicated food, the server's copy under a new id, or a food just read
   * before `beginEditingDraft()` wraps it.
   *
   * Clears the draft holder in the same step. A holder left standing would make save()
   * merge it back — putting the OLD document on screen and discarding the new food and
   * every edit made since, silently — and would aim deleteFoodAsync() at the old
   * document. That is #5086, which reached a customer through the Duplicate action.
   *
   * Clears `foodWasDeleted` for the same reason, one step further on: it is a fact about the
   * document we WERE editing, and this method is the moment we stop editing that one. Left
   * standing it refuses to save a food that was never deleted -- Delete, then Duplicate from
   * the still-live menu, gives the page a brand-new copy, and Accept would throw the user's
   * work away and report a delete against the copy's id (df-32c03324a817's review, round 7).
   */
  private setEditedFood(food: Food | Recipe) {
    this.food = food;
    this.foodDraft = undefined;
    this.foodWasDeleted = false;
  }

  /**
   * Start editing a document shared through the database's identity map: the form binds
   * to a private draft, and save() merges it back into `canonical`.
   */
  private beginEditingDraft(canonical: Food | Recipe) {
    this.foodDraft = new CouchDocDraft(canonical);
    this.food = this.foodDraft.draft;
  }

  // build a shareable URL for the given food using the real app domain (not localhost on native)
  private buildShareUrl(foodId: string): string {
    const path = pagesToUrl([{ segment: 'food', params: { id: foodId, mode: 'view' } }]);
    return `${getSharedUrlOrigin()}${path}`;
  }

  // show the system share sheet for a food URL, fall back to clipboard if share is unavailable
  private async showShareSheet(url: string, title: string) {
    const result = await trySystemShare({ title, url });
    if (result !== 'unavailable') return;
    // if share not supported, fall back to clipboard
    await navigator.clipboard.writeText(url);
    this.toastController.showToast({
      message: LL.editFood.buttons.linkCopied(),
      type: ToastType.SuccessLight,
      duration: 2000,
    });
  }

  protected handleShareIconClick(event: Event) {
    if (this.isImportableRecipe) {
      lockClick(event, this.shareFoodLink(this.food._id));
    } else if (this.isImportedRecipe) {
      lockClick(event, this.shareFoodLink(this.food.local.sourceRecipeId));
    } else {
      lockClick(event, this.handleShareRecipe());
    }
  }

  // build a share URL for the given food and present the browser share sheet
  // spinner covers the variable delay before the OS share sheet appears
  private async shareFoodLink(foodId: string) {
    const loading = this.loadingCtrl.create();
    await loading.present();
    try {
      const url = this.buildShareUrl(foodId);
      await this.showShareSheet(url, this.food.name);
    } finally {
      void loading.dismiss();
    }
  }

  /**
   * Publish the shared recipe's missing ingredients and answer how many could not be
   * published (#3255). The rules are on `publishMissingRecipeIngredientsAsync`. A walk
   * that stopped part-way counts as one failure, so the user is told some ingredients did
   * not go out; the start-up repair tries the recipe again next time.
   */
  private async publishMissingIngredientsAsync(recipe: Recipe): Promise<number> {
    try {
      return await this.foodService.publishMissingRecipeIngredientsAsync(recipe);
    } catch (e) {
      // offline or the database recovering is expected here; the toast is enough
      if (!(isNetworkError(e) || isDatabaseUnavailable(e) || e instanceof FoodReadFailedError)) {
        this.logging.trackSilentProblem('recipe-ingredient-publish: could not check ingredients', { recipeId: recipe._id, detail: describeError(e) });
      }
      return 1;
    }
  }

  async handleShareRecipe() {
    // this page has already deleted the recipe it was editing (df-e3f98d757250). Sharing it now
    // would write the deleted document back AND publish it into the global database -- while the
    // delete's own unpublish is still retrying to take it out of there, so the two would race.
    // Close the editor instead, for the reason voteAsync gives. The helper reports the attempt.
    if (this.mustNotWriteDeletedFood()) {
      await this.closeDeletedFoodEditorAsync();
      return;
    }
    if (!this.food.local) this.food.local = {};

    // if first share, publish recipe and its ingredients to global_foods
    if (!this.food.local.shared) {
      const loading = this.loadingCtrl.create();
      await loading.present();

      let failedIngredientCount = 0;
      let recipePublished = false;
      try {
        this.food.local.shared = true;
        void this.foodService.saveUserFoodAsync(this.food, 'used');

        // publish every ingredient not yet in global_foods, sub-recipes included (#3255)
        if (isRecipe(this.food)) {
          failedIngredientCount = await this.publishMissingIngredientsAsync(this.food);
          if (this.viewDestroyed) return;
        }

        // publish the recipe itself
        const result = await this.cloudFunctionsService.publishFoodAsync({ food: this.food, adminMode: false });
        if (this.viewDestroyed) return;
        if (result.error) {
          console.error(`Can't publish shared recipe ${this.food._id} - ${result.error}`);
        } else {
          await this.adoptSharedRecipeCopyIfForkedAsync(result);
          recipePublished = true;
        }
      } catch (e) {
        // the person is told by the "share failed" toast below; this tells us
        this.logging.trackSilentProblem('RecipeShareFailed', { foodId: this.food._id, detail: describeError(e) });
      } finally {
        // if recipe publish failed (but not view-destroyed — server may have processed it), revert
        if (!recipePublished && !this.viewDestroyed) {
          this.food.local.shared = false;
          void this.foodService.saveUserFoodAsync(this.food, 'used');
        }
        void loading.dismiss();
      }

      if (this.viewDestroyed) return;

      // if recipe publish failed, inform user and don't show share sheet
      if (!recipePublished) {
        this.toastController.showToast({
          message: LL.editFood.buttons.shareFailed(),
          duration: 4000,
        });
        return;
      }

      if (failedIngredientCount > 0) {
        this.toastController.showToast({
          message: LL.editFood.buttons.shareIngredientsFailed(),
          duration: 4000,
        });
      }
    }

    // shareFoodLink has its own loading spinner
    await this.shareFoodLink(this.food._id);
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  /**
   * Take a shared recipe back out of the global, searchable food database (df-667659334ae3).
   *
   * The call fails in two different ways and they are NOT the same:
   *
   * - it throws, once the resilient retry policy has given up. We do not know what the
   *   server did, so the recipe stays marked as shared and the user is asked to try again.
   * - it returns `{ error }`. `unpublishFood_impl` answers that way for a recipe the global
   *   database has never seen, for our own official content, for a recipe owned by somebody
   *   else, and for a CouchDB delete that failed. Only some of those mean the recipe is
   *   still out there, so the answer is classified the same way the delete path classifies
   *   it — see `unpublish-outcome.ts`.
   *
   * Reading only the thrown half is what this method used to do, and it left the user told
   * the recipe was private while it was still published for everybody.
   */
  async handleUnshareRecipe() {
    // this page has already deleted the recipe it was editing (df-e3f98d757250). Unsharing ends
    // by writing the recipe back with `shared` cleared, which would resurrect the deleted
    // document -- and the delete's own unpublish is already taking it out of the global
    // database, so there is nothing here left to do either. Close the editor instead, for the
    // reason voteAsync gives. The helper reports the attempt.
    if (this.mustNotWriteDeletedFood()) {
      await this.closeDeletedFoodEditorAsync();
      return;
    }
    if (!this.food.local) this.food.local = {};
    let failure: string | undefined;
    try {
      const result = await this.cloudFunctionsService.unpublishFoodAsync({ food: this.food, adminMode: false });
      failure = result.error;
    } catch (e) {
      console.error(`Error unsharing recipe ${this.food._id}:`, e);
      this.toastController.showToast({
        message: LL.editFood.buttons.unshareFailed(),
        duration: 4000,
      });
      return;
    }
    // the food still carries `local.shared`, so `wasFoodPublishedByThisUser` judges it as the
    // shared recipe it currently is. Clear the flag only once we know nothing of ours is left
    // in the global database.
    //
    // this runs BEFORE the `viewDestroyed` check on purpose. The call is a resilient one, so it
    // can still be retrying minutes after the user left this screen — and a recipe left
    // published is worth reporting whether or not there is still a screen to tell them on.
    const outcome = describeUnpublishOutcome(this.food, failure !== undefined);
    if (outcome === UnpublishOutcome.StillPublished) {
      // the recipe is in the global database and our request to remove it did not work. Leave
      // it marked as shared so the Unshare button stays and the user can try again, and report
      // it: nothing else would ever notice a recipe left published against the user's wishes.
      this.logging.trackSilentProblem('food-unshare.failed-while-published', {
        foodId: this.food._id,
        isRecipe: isRecipe(this.food),
        error: failure,
      });
      if (this.viewDestroyed) return;
      this.toastController.showToast({
        message: LL.editFood.buttons.unshareFailed(),
        duration: 4000,
      });
      this.cd.markForCheck();
      return;
    }
    if (this.viewDestroyed) return;
    if (outcome === UnpublishOutcome.NothingToRemove) {
      // this user's own publish never put the recipe in the global database, so there was
      // nothing there to take out and the end state is the one the user asked for. Not worth a
      // message: refusing here would leave a recipe marked as shared that can never be
      // unshared.
      console.log(`Recipe ${this.food._id} was not published by this user, so there was nothing to unshare - ${failure}`);
    }
    this.food.local.shared = false;
    void this.foodService.saveUserFoodAsync(this.food, 'used');
    this.cd.markForCheck();
  }


  private async checkForOriginalUpdateAsync() {
    const sourceId = this.food.local?.sourceRecipeId;
    if (!sourceId) return;
    try {
      const original = await this.foodService.getFoodByIdAsync(sourceId, { cacheFood: false, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
      if (this.viewDestroyed) return;
      if (!original) {
        this.originalDeleted = true;
        this.cd.markForCheck();
        return;
      }
      if (original._rev !== this.food.local.sourceRevision) {
        this.hasOriginalUpdate = true;
        this.cd.markForCheck();
      }
    } catch (err) {
      // non-critical: the banner is only a hint. A network drop is expected and stays
      // silent; anything else is reported so a broken check does not go unnoticed.
      if (!isTransientFetchError(err)) {
        this.logging.trackSilentProblem('RecipeOriginalCheckFailed', { sourceId, detail: describeError(err) });
      }
    }
  }

  handleRefreshFromOriginal() {
    void this.handleRefreshFromOriginalAsync();
  }

  private async handleRefreshFromOriginalAsync() {
    // this page has already deleted the imported recipe it was editing (df-e3f98d757250).
    // Refreshing it from the original ends by writing it back, which would resurrect the deleted
    // document -- and there is nothing to refresh, because the copy this page held is gone.
    // Close the editor instead, for the reason voteAsync gives. The helper reports the attempt.
    if (this.mustNotWriteDeletedFood()) {
      await this.closeDeletedFoodEditorAsync();
      return;
    }
    const loading = this.loadingCtrl.create();
    void loading.present();

    const sourceId = this.food.local.sourceRecipeId;
    const original = await this.foodService.getFoodByIdAsync(sourceId, { cacheFood: false, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (this.viewDestroyed) { void loading.dismiss(); return; }

    if (!original) {
      this.originalDeleted = true;
      this.hasOriginalUpdate = false;
      void loading.dismiss();
      this.cd.markForCheck();
      return;
    }

    // preserve local identity and import metadata
    const savedSourceRecipeId = this.food.local.sourceRecipeId;
    deepOverwriteCouchSafe(this.food, original);
    if (!this.food.local) this.food.local = {};
    this.food.local.sourceRecipeId = savedSourceRecipeId;
    this.food.local.sourceRevision = original._rev;

    void this.foodService.saveUserFoodAsync(this.food, 'used');
    void loading.dismiss();

    this.hasOriginalUpdate = false;
    this.toastController.showToast({
      message: LL.editFood.buttons.recipeRefreshed(),
      type: ToastType.SuccessLight,
      duration: 2000,
    });
    this.updateDisplayNutrients();
    this.cd.markForCheck();
  }

  handleImportRecipe(event: Event) {
    lockClick(event, this.handleImportRecipeAsync());
  }

  private async handleImportRecipeAsync() {
    const loading = this.loadingCtrl.create();
    void loading.present();

    const importedRecipe = this.foodService.duplicateFood(this.food) as Recipe;
    // remove " (copy)" suffix — this is an import, not a duplicate
    importedRecipe.name = importedRecipe.name.replace(/ \(copy\)$/, '');
    if (!importedRecipe.local) importedRecipe.local = {};
    importedRecipe.local.sourceRecipeId = this.food._id;
    importedRecipe.local.sourceRevision = this.food._rev;

    void this.foodService.saveUserFoodAsync(importedRecipe, 'used');
    void loading.dismiss();

    this.toastController.showToast({
      message: LL.editFood.buttons.recipeImported(),
      type: ToastType.SuccessLight,
      duration: 2000,
    });

    // navigate to the imported copy (replace current page to avoid pop-on-empty-stack reload)
    void this.navCtrl.pushAsync(IEditFoodPage, { id: importedRecipe._id, mode: 'view' });
  }

  handleAcceptButtonClicked(event: Event, buttonType: ButtonType) {
    this.telemetry.trackEvent('edit_food_accept_button_click', { buttonType: buttonType });
    lockClick(event, this.handleAcceptButton());
  }

  handleEditButtonClicked(buttonType: ButtonType) {
    this.telemetry.trackEvent('edit_food_edit_button_click', { buttonType: buttonType });
    this.setReadonly(false)
  }

  async handleBarCodeClick(ean: string) {
    let accepted = await this.alertCtrl.showAlertAsync({
      title: LL.editFood.alerts.removeBarcode(),
      message: LL.editFood.alerts.removeBarcodeMessage({ barcode: ean }),
      buttons: [
        {
          text: LL.common.buttons.remove(),
          role: AlertButtonRole.Primary,
          result: true,
        },
        {
          text: LL.common.buttons.cancel(),
          role: AlertButtonRole.Cancel,
        },
      ],
    });

    if (accepted) {
      this.food.EANs = this.food.EANs.filter(e => e !== ean);
      this.cd.markForCheck();
    }
  }


  async handleAddBarcodeClick() {
    void this.navCtrl.pushAsync(IBarcodeScannerPage, { returnCallback: 'onBarcodeScannedAsync' });
  }

  async onBarcodeScannedAsync(_context: unknown, code: string) {
    // if page was closed without scanning (e.g. header back button), ignore
    if (!code) return;
    this.telemetry.trackEvent('barcode_added_to_food', { ean: code });
    if (!this.food.EANs) {
      this.food.EANs = [];
    }
    if (this.food.EANs.includes(code)) {
      this.toastController.showToast({ message: LL.editFood.messages.barcodeExists(), duration: 2000 });
    } else {
      this.food.EANs.push(code);
      this.cd.markForCheck();
    }
  }

  async handleFoodSourceClick(event?: Event) {
    // a staple lists the foods it was built from, each one tappable (food staples plan 4a)
    if (this.foodSource.source === FoodSource.STP && await this.showStapleSourcesAsync(event)) {
      return;
    }
    const footerByTrustLevel = {
      ['official']: LL.editFood.trustLevel.official(),
      ['crowdsource']: LL.editFood.trustLevel.crowdsource(),
      ['community']: LL.editFood.trustLevel.community(),
    };
    const footer = footerByTrustLevel[this.foodSource.trustLevel] || '';
    // getFoodSourceDisplay decides per source whether the text is our own copy
    // (translated) or a third-party citation (reproduced as published) — see
    // its docblock. The two pieces are composed through a catalog key rather
    // than concatenated here, so a translation can adjust the spacing.
    const display = getFoodSourceDisplay(this.foodSource.source);
    await this.alertCtrl.showAlertAsync({
      title: display.title,
      messageMarkdown: LL.editFood.alerts.foodSourceBody({
        attribution: display.attribution,
        trustNote: footer,
      }),
    });
  }

  /**
   * Show a staple's source foods as a list; tapping one opens it. Returns false when the staple
   * carries no usable source ids, so the caller shows the plain source dialog instead.
   */
  private async showStapleSourcesAsync(event: Event | undefined): Promise<boolean> {
    const stapleId = this.food._id;
    // repair the stored id list (not a list, foreign ids, repeats) before reading; no names are needed yet
    const ids = buildStapleSourceRows(this.food.staple?.sourceFoodIds, new Map()).map(row => row.id);
    if (ids.length === 0) {
      return false;
    }
    // read the names without copying the foods into the user's own database
    const foodsById = await this.foodService.getFoodsByIdsFromGlobalDbAsync(ids);
    if (this.viewDestroyed) {
      return true;
    }
    const rows = buildStapleSourceRows(ids, foodsById);
    const missingIds = rows.filter(row => !row.canOpen).map(row => row.id);
    // if any source could not be read, report it once per tap; a failed read of the whole list is
    // reported by the read itself, and then every source shows up here as missing too
    if (missingIds.length > 0) {
      this.logging.trackSilentProblem('staple source food missing', { stapleId, sourceIds: missingIds, readAnything: foodsById.size > 0 });
    }
    const buttons: PopupMenuButton[] = [];
    for (const row of rows) {
      buttons.push({
        text: LL.editFood.sourceFoods.row({
          name: row.name ?? LL.editFood.sourceFoods.nameUnavailable(),
          database: getFoodSourceShortName(row.source),
        }),
        value: row.id,
        icon: row.canOpen ? 'arrow-dropright' : undefined,
        handler: row.canOpen ? () => void this.navCtrl.pushAsync(IEditFoodPage, { id: row.id, mode: 'view' }) : undefined,
      });
    }
    await this.popupMenu.createMenu({
      title: getFoodSourceDisplay(FoodSource.STP).title,
      subsections: [{ header: LL.editFood.sourceFoods.header(), buttons }],
      targetFromElement: event?.currentTarget instanceof HTMLElement ? event.currentTarget : undefined,
    });
    return true;
  }

  handleLogFoodButtonClick() {
    this.telemetry.trackEvent('edit_food_log_food', {});

    // if a page underneath is adding this food somewhere, hand the food back so it finishes that
    // errand -- logging to today on top of this screen ignored the meal or day the user was on (#5520)
    if (this.navParams.data.logReturnsToCaller) {
      const result: FoodDetailsResult = { exit: FoodDetailsExit.Log, foodId: this.food._id };
      void this.navCtrl.popAsync(result);
      return;
    }

    const currentDay = this.debug.getNowLocal().startOf('day');
    void this.navCtrl.pushAsync(IEditIngredientPage, buildLogPortionScreenParams({
      dayDate: currentDay.toISODate(),
      pickedId: this.food._id,
    }));
  }

  /**
   * Answer from the portion screen. Declared as the list's EARLY handler, so this runs before
   * that screen's closing transition and must not navigate — see `handleEditResult` on
   * `ingredient-list`, and `returnCallbackImmediate` on CsPage.
   */
  onIngredientEdited(context: IngredientEditReturnContext, result: EditIngredientResult) {
    this.ingredientsCard.handleEditResult(context, result);
  }

  // handler for FoodsPage returnCallback - delegates to ingredient-list
  /**
   * Answer from the Foods page — hand it to the ingredient list.
   *
   * Returns the list's promise rather than dropping it (#4967). The Foods page can now stay
   * OPEN while this runs, and it needs to know when the work has finished: a pick that ends
   * up opening the portion screen must leave the list refusing further taps, while one that
   * gives up part-way must let the user try again. Dropping the promise made both look
   * identical, and the list released its guard immediately on every pick.
   */
  async handleFoodPageResult(context: { source?: string }, result: FoodsPageResult) {
    if (!result) return;

    // delegate to ingredient-list
    if (context?.source === 'addFood' || context?.source === 'viewAllRecentMeals') {
      await this.ingredientsCard.handleFoodPageResult(context as { source: string }, result);
    }
  }

  // View mode helper methods

  getServingSizesForDisplay(): string[] {
    return buildServingSizeTexts(this.food, this.settings.locale);
  }

  getMacrosPerLabel(): string {
    return buildMacrosPerLabel(this.food, this.macrosDisplayMode);
  }

  getServingSegmentLabel(): string {
    return buildServingSegmentLabel(this.food);
  }

  // Get nutrition header description
  // Format controlled by label type (US/EU), value controlled by per toggle (macrosDisplayMode)
  getNutritionHeaderDescription(): string {
    return buildNutritionHeaderDescription({
      food: this.food,
      labelStyle: this.settings.nutritionLabelStyle,
      mode: this.macrosDisplayMode,
      standardAmount: this.STD_AMOUNT,
      locale: this.settings.locale,
    });
  }

  getNutrientDisplayValue(entry: NutritionLabelEntry): string {
    return formatNutrientDisplayValue(entry);
  }

  getEnergyDisplayValue(): string {
    return formatEnergyDisplayValue(this.getDisplayedNutrients(), this.settings.locale);
  }

  getOptionalNutrients(): UiDisplayNutrient[] {
    return selectOptionalNutrients(this.getDisplayedNutrients(), this.nutritionLabelOrderMain);
  }

  toggleAdditionalNutrients(): void {
    this.showAdditionalNutrients = !this.showAdditionalNutrients;
    this.cd.markForCheck();
  }

  handleEditServingSizesClick(): void {
    this.setReadonly(false);
    this.telemetry.trackEvent('edit_food_edit_serving_sizes_click', {});
  }

  handleEditBarcodeClick(): void {
    this.setReadonly(false);
    this.telemetry.trackEvent('edit_food_edit_barcode_click', {});
  }

  handleEditNutrientsClick(): void {
    this.setReadonly(false);
    this.telemetry.trackEvent('edit_food_edit_nutrients_click', {});
  }

  onMacrosDisplayModeChange(): void {
    // update the displayed nutrients based on the new mode
    this.updateDisplayNutrients();
    this.setNutritionLabelOrder(false); // don't reset since user just changed the toggle
    this.cd.markForCheck();
  }

  async handleMoreOptionsClick(event: Event): Promise<void> {
    const buttons: PopupMenuButton[] = [
      {
        text: LL.editFood.buttons.editFood(),
        icon: 'pencil',
        handler: () => this.setReadonly(false),
      },
      {
        text: LL.editFood.buttons.duplicate(),
        icon: 'copy',
        handler: () => this.handleDuplicateOption(),
      },
      {
        text: LL.editFood.buttons.delete(),
        icon: 'trash',
        handler: (event) => { if (event) lockClick(event, this.handleDeleteOption()); },
      },
    ];

    // share/unshare options for the user's own recipes, which stay their own after an unshare (df-64bf2e6f1651)
    const shareRole = recipeShareRole(this.food);
    if (shareRole === RecipeShareRole.Own) {
      buttons.push({
        text: LL.editFood.buttons.share(),
        icon: 'link',
        handler: (event) => { if (event) lockClick(event, this.handleShareRecipe()); },
      });
      if (this.food.local?.shared) {
        buttons.push({
          text: LL.editFood.buttons.unshare(),
          icon: 'link-off',
          handler: (event) => { if (event) lockClick(event, this.handleUnshareRecipe()); },
        });
      }
    } else if (shareRole === RecipeShareRole.Imported) {
      // imported recipe: share link to the original
      buttons.push({
        text: LL.editFood.buttons.share(),
        icon: 'link',
        handler: (event) => { if (event) lockClick(event, this.shareFoodLink(this.food.local.sourceRecipeId)); },
      });
    } else if (shareRole === RecipeShareRole.Importable) {
      // viewing someone else's shared recipe: share link to it
      buttons.push({
        text: LL.editFood.buttons.share(),
        icon: 'link',
        handler: (event) => { if (event) lockClick(event, this.shareFoodLink(this.food._id)); },
      });
    }

    // if the food is not the user's own, offer Report -- the one place it is offered since #5520
    if (canReportFood(this.food)) {
      buttons.push({
        text: LL.flagContent.menuButton(),
        icon: 'flag',
        handler: () => void this.flagContentService.showFlagDialogAsync(this.food),
      });
    }

    await this.popupMenu.createMenu({
      targetFromElement: event.currentTarget as HTMLElement,
      buttons,
    });
  }

  // ── Recipe Scanner (#3709) ─────────────────────────────────────────

  // the dedicated create-mode ⋯ menu — a single "Scan Recipe" item. This is a
  // separate menu from handleMoreOptionsClick, whose view-mode items (Edit Food
  // / Duplicate / Delete / Share) make no sense on a brand-new unsaved recipe.
  async handleCreateRecipeMenuClick(event: Event): Promise<void> {
    const buttons: PopupMenuButton[] = [
      {
        text: LL.editFood.buttons.scanRecipe(),
        icon: 'camera',
        handler: () => this.openRecipeScanner(),
      },
    ];
    await this.popupMenu.createMenu({
      targetFromElement: event.currentTarget as HTMLElement,
      buttons,
    });
  }

  protected openRecipeScanner() {
    this.telemetry.trackEvent('recipe_scan_open', {});
    void this.navCtrl.pushAsync(IRecipeScannerPage, {
      returnCallback: 'onRecipeScanResult',
    });
  }

  // apply the scanned recipe draft to this.food IN MEMORY — nothing is saved.
  // Mirrors edit-meal.onMealPhotoScanResult for the ingredient build.
  async onRecipeScanResult(_context: unknown, result: RecipeScannerResult) {
    if (!result || !isRecipe(this.food)) return;

    // name — only fill if the user hasn't already typed one
    if (result.name && !this.food.name?.trim()) {
      this.food.name = result.name;
    }

    // instructions — set the extracted markdown steps
    if (result.instructions) {
      this.food.instructions = result.instructions;
    }

    // ingredients — append matched items as Real ingredients. Replace the array
    // (new reference) so the OnPush ingredient list re-renders.
    if (result.matchedIngredients?.length) {
      const newIngredients: Ingredient[] = [...this.food.ingredients];
      for (const matched of result.matchedIngredients) {
        const food = await this.foodService.getFoodByIdAsync(matched.food._id, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
        if (!food) {
          // abnormal: the food was just matched on the scanner results screen, so a
          // null fetch here means it vanished between scan and apply — surface it and
          // skip just this ingredient rather than failing the whole apply
          this.logging.trackSilentProblem('Recipe-scan apply: matched food not found', { foodId: matched.food._id });
          continue;
        }
        const ingredient: IngredientReal = {
          type: IngredientType.Real,
          foodId: food._id,
          name: food.name,
          weight_g: matched.estimatedGrams,
          // keep the "Weighed with peel" tick from the scanner's portion screen (#5544)
          ...(matched.weighedWithRefuse ? { weighedWithRefuse: matched.weighedWithRefuse } : {}),
        };
        this.foodService.setIngredientStatus(ingredient);
        newIngredients.push(ingredient);
        void this.foodService.markFoodAsUsedAsync(food);
      }
      this.food.ingredients = newIngredients;
    }

    if (this.viewDestroyed) return;
    // recompute total weight + nutrients via the ingredient list, which emits
    // the updated totals back onto this.food through the existing handlers
    const loadResult = await this.ingredientsCard.loadIngredientsAsync();
    if (this.viewDestroyed) return;

    // servings — store as the recipe's 'prepared' measure amount (N servings; per-serving
    // weight = totalWeight / amount). Done AFTER loadIngredientsAsync so totalWeight_g is
    // current. Only when > 1: a single serving is the default (no measure needed).
    // if the load did not finish, totalWeight_g was never recalculated, so skip the write
    // rather than store a servings weight worked out from a stale total or from 0 (#4547).
    // Only this write is skipped, not the rest of the method: the recipe image below must
    // still be consumed and cleared, or it leaks into the next scan.
    if (loadResult === IngredientListLoadResult.Completed && result.servings && result.servings > 1) {
      this.food.measures = [{ name: RECIPE_SERVINGS_MEASURE_NAME, weight_g: this.food.totalWeight_g || 0, amount: result.servings }];
    }

    // recipe image — the scanner stashes the AI-chosen cooked-dish photo (if any) in the
    // in-memory holder (too large for nav params). Consume + clear it; apply it only if
    // the user hasn't already added an image, so a scan never clobbers a chosen photo.
    const pendingImageDataUrl = pendingRecipeScanImageDataUrl;
    setPendingRecipeScanImage(null);
    if (pendingImageDataUrl && !this.hasImage()) {
      void this.uploadRecipeScanImageAsync(pendingImageDataUrl);
    }

    this.cd.markForCheck();
  }

  // process the AI-chosen cooked-dish photo into the recipe's image (#3709). Best-effort:
  // a failure must surface to telemetry but must not block the in-memory apply.
  private async uploadRecipeScanImageAsync(dataUrl: string) {
    try {
      const scaledImage = await this.imageService.processDataUrlAsync(dataUrl, 'food-images', true);
      if (this.viewDestroyed) return;
      this.food.image = scaledImage;
      this.cd.markForCheck();
    } catch (err) {
      this.logging.trackSilentProblem('RecipeScanImageProcessFailed', { error: describeError(err) });
    }
  }
}
